// ─────────────────────────────────────────────────────────────────────────────
// panels.tsx — the Overview's banner, seven panels and the bottom pulse ticker.
//
// TWO ARE LIVE, FIVE ARE HONESTLY EMPTY, and the difference is visible:
//
//   2 · activity pulse   LIVE  — /api/rexy/activity, scoped to this domain
//   4 · agent roster     LIVE  — the registry projection from /api/os/structure
//   1 · KPI row          empty — board.overview
//   3 · budget rollup    empty — board.pricing_budgets
//   5 · CEO report       empty — board.ceo
//   6 · telco rollup     empty — board.supporting_systems
//   7 · API change watch empty — board.integrations
//
// ABSENT IS NOT BROKEN AND DORMANT IS NOT BROKEN. The board store does not exist
// until the first PUT; GET answers {} and every board-backed panel renders its
// empty state. The pulse reads DORMANT at rate 0 and says DORMANT — a live source
// truthfully reporting no traffic, which is a different fact from a panel that
// could not load and must never share its styling.
//
// THE SHAPE IS THE DESIGN'S; EVERY FIGURE IS READ OR ABSENT. The reference
// image's numbers are sample data (owner ruling, this session). A tile with no
// source reads "—". Nothing here is populated to make the layout look finished.
//
// No fetch lives in this file. Feeds arrive as props from the shared core.
// ─────────────────────────────────────────────────────────────────────────────

import React, { useMemo, useState } from 'react';
import type { ActivityIndexResponse, ActivityRow, StructureAgent, DomainBoard } from '../../data/dashboard';
import { ACTIVITY_KIND_LABEL, GROWTH_LANE } from '../../data/dashboard';
import ActivityPanel from '../dashboard/ActivityPanel';
import { KPI_TILES, PANEL_ROOT, cellTone, own } from './presentation';
import { LaneFilter } from './LaneFilter';
import {
  LANE_ALL, applyLane, laneFilterable, regionOptions, sectorOptions,
} from './laneFilter';
import type { Visual } from './presentation';
import type { PanelSpec, PanelBody } from './surface';
import { DiscoveryMap } from './DiscoveryMap';
import {
  WORKBOOK_FILE, WORKBOOK_FORMULA, WORKBOOK_ACTIONS, WORKBOOK_STATUS,
  SCORE_COLUMNS, WB,
} from './workbook';
import { MC, MC_FONT, DOT } from './theme';
import { rosterDotState, rosterDotLabel, ceoBannerState } from './rosterState';
import type { RosterDotState, CeoHealth, RootDataState } from './rosterState';
import { callRowOf, pressCallRow } from '../../lib/deltaCallRow';
import { placeDirections, directionsForLine, directionsForBranch } from '../../lib/scriptDirections';

// ── the orb ──────────────────────────────────────────────────────────────────
// The brand mark, drawn as SVG so it costs no asset and no fetch. Teal→violet
// wash with a soft outer glow: dimensional against the dark canvas, not a flat
// disc, and not a bitmap that would have to be shipped alongside the bundle.
export function Orb({ size = 38 }: { size?: number }) {
  const id = 'mcorb';
  return (
    <svg width={size} height={size} viewBox="0 0 40 40" aria-hidden="true" style={{ flexShrink: 0 }}>
      <defs>
        <radialGradient id={`${id}-core`} cx="38%" cy="32%" r="72%">
          <stop offset="0%" stopColor={MC.teal} stopOpacity="0.95" />
          <stop offset="55%" stopColor={MC.blue} stopOpacity="0.65" />
          <stop offset="100%" stopColor={MC.violet} stopOpacity="0.30" />
        </radialGradient>
        <radialGradient id={`${id}-glow`} cx="50%" cy="50%" r="50%">
          <stop offset="55%" stopColor={MC.teal} stopOpacity="0.22" />
          <stop offset="100%" stopColor={MC.teal} stopOpacity="0" />
        </radialGradient>
      </defs>
      <circle cx="20" cy="20" r="19" fill={`url(#${id}-glow)`} />
      <circle cx="20" cy="20" r="12.5" fill={`url(#${id}-core)`} />
      <circle cx="20" cy="20" r="12.5" fill="none" stroke={MC.teal} strokeOpacity="0.45" strokeWidth="1" />
      <ellipse cx="16.5" cy="15.5" rx="4.6" ry="3.1" fill="#ffffff" opacity="0.16" />
    </svg>
  );
}

// ── shared panel chrome ──────────────────────────────────────────────────────
// Dimensional, never flat (CLAUDE.md UI §3): raised card over the dark canvas,
// soft drop shadow plus a 1px top-edge highlight — depth by shadow falloff, never
// by a brighter surface. Radius is the design's --radius, sampled.
export function Panel({ title, status, accent, note, badge, panelKey, children }: {
  title: string; status?: React.ReactNode; accent?: string;
  note?: string; badge?: string; panelKey?: string; children: React.ReactNode;
}) {
  // THE BADGE IS A VALUE IN A SLOT LIKE ANY OTHER. Its visual comes from the
  // same single entry point a table cell, a ledger state and a question tag use
  // — element AND tone, because the design draws three of these as pills and
  // the rest as badges. Degrade is the design's OWN default: a badge the
  // vocabulary does not know renders as `class="badge"` with no colour, which
  // is what the design's 18 untoned badges already are. It is never dropped.
  const vis = panelKey && badge ? cellTone(panelKey, 'badge', badge) : null;
  return (
    <section style={S.panel}>
      <header style={S.panelHead}>
        <span style={S.panelTitleWrap}>
          <span style={{ ...S.panelBar, background: accent || MC.blue }} />
          <span style={S.panelTitleCol}>
            <span style={S.panelTitle}>{title}</span>
            {note && <span style={S.panelNote}>{note}</span>}
          </span>
        </span>
        <span style={S.panelRight}>
          {badge && <span style={PILL[vis || 'badgePlain']}>{badge}</span>}
          {status}
        </span>
      </header>
      {children}
    </section>
  );
}

/** The one empty-state voice. Says WHAT is absent and WHY that is correct. */
export function EmptyState({ what }: { what: string }) {
  return <div style={S.empty}>no {what} yet — this panel fills when its page ships</div>;
}

function Chip({ text, tone }: { text: string; tone: 'live' | 'dormant' | 'empty' | 'failed' }) {
  const color = tone === 'live' ? MC.green : tone === 'dormant' ? MC.amber
              : tone === 'failed' ? MC.red : MC.textDim;
  const bg = tone === 'live' ? MC.greenSoft : tone === 'dormant' ? MC.amberSoft
           : tone === 'failed' ? MC.redSoft : 'transparent';
  return (
    <span style={{ ...S.chip, color, borderColor: color, background: bg }}>
      <span style={{ ...S.chipDot, background: color }} />
      {text}
    </span>
  );
}

// ── the Overview banner ──────────────────────────────────────────────────────
// The design's banner across the top of the well. Every figure in it is a COUNT
// of what the API returned — no transcribed sample number reaches this strip.
export function OverviewBanner({ domain, roots, agents, ready }: {
  domain: string; roots: number; agents: number; ready: boolean;
}) {
  return (
    <section style={S.banner}>
      <div style={S.bannerGlow} />
      <div style={S.bannerText}>
        <div style={S.bannerTitle}>{domain} Overview</div>
        <div style={S.bannerSub}>
          {ready
            ? `${roots} declared root${roots === 1 ? '' : 's'} · ${agents} registered agent${agents === 1 ? '' : 's'} · board store empty until its first write`
            : 'reading the declaration…'}
        </div>
      </div>
      <div style={S.bannerRight}>
        <span style={{ ...S.dot, background: ready ? DOT.live : DOT.idle }} />
        <span style={S.bannerState}>{ready ? 'READ' : 'READING'}</span>
      </div>
    </section>
  );
}

// ── 1 · KPI row ──────────────────────────────────────────────────────────────
export function KpiRow({ board }: { board: DomainBoard }) {
  const root = (board[PANEL_ROOT.kpis] || {}) as Record<string, unknown>;
  const anyValue = KPI_TILES.some((t) => root[t.key] !== undefined);
  return (
    <Panel
      title="KPIs"
      accent={MC.teal}
      status={anyValue ? <Chip text="live" tone="live" /> : <Chip text="awaiting first write" tone="empty" />}
    >
      <div style={S.kpiGrid}>
        {KPI_TILES.map((t) => {
          const v = root[t.key];
          const has = typeof v === 'number' || typeof v === 'string';
          return (
            <div key={t.key} style={S.kpiTile}>
              <div style={S.kpiLabel}>{t.label}</div>
              <div style={{ ...S.kpiValue, color: has ? MC.teal : MC.textFaint }}>
                {has ? String(v) : '—'}
              </div>
            </div>
          );
        })}
      </div>
    </Panel>
  );
}

// ── 2 · all-receptionist activity pulse (LIVE) ───────────────────────────────
export function ActivityPulse({ domain, index, err }: {
  domain: string; index: ActivityIndexResponse | null; err: string | null;
}) {
  const groups = index?.groups || [];
  const rows: ActivityRow[] = groups
    .flatMap((g) => g.rows)
    .filter((r) => (r.domain || '').toUpperCase() === domain.toUpperCase());

  // RATE 0 IS DORMANT, AND DORMANT IS A LIVE ANSWER. The source answered; it
  // reported no traffic in the window. Amber, not grey — never the failure voice.
  const dormant = !err && index !== null && rows.length === 0;

  // The chart is the hour groups the index ALREADY carries — one bar per slot,
  // height = this domain's rows in that hour. Bucketing is not invented here;
  // the pulse's own slots are the x-axis.
  const bars = groups.map((g) => ({
    label: g.label,
    n: (g.rows || []).filter((r) => (r.domain || '').toUpperCase() === domain.toUpperCase()).length,
  }));
  const peak = Math.max(1, ...bars.map((b) => b.n));

  return (
    <Panel
      title="Activity pulse"
      accent={MC.green}
      status={
        err ? <Chip text="source unavailable" tone="failed" />
          : dormant ? <Chip text="dormant · rate 0" tone="dormant" />
          : <Chip text={`live · ${rows.length} rows`} tone="live" />
      }
    >
      {err ? (
        <div style={S.failed}>could not read the activity index — {err}</div>
      ) : index === null ? (
        <div style={S.empty}>reading…</div>
      ) : (
        <>
          {/* The chart renders in BOTH states. A dormant domain draws its hour
              slots at a flat baseline — the shape of "measured, and it was zero",
              which is a different picture from a panel that could not load. */}
          <div style={S.chart}>
            {bars.length === 0 ? (
              <div style={S.empty}>the pulse has no hour slots in this window</div>
            ) : bars.map((b, i) => (
              <div key={i} style={S.barCol} title={`${b.label} · ${b.n}`}>
                <div style={{
                  ...S.bar,
                  height: b.n === 0 ? 2 : `${Math.max(6, (b.n / peak) * 100)}%`,
                  background: b.n === 0 ? MC.textFaint : MC.green,
                  boxShadow: b.n === 0 ? 'none' : `0 0 10px ${MC.greenSoft}`,
                }} />
              </div>
            ))}
          </div>
          {dormant ? (
            <div style={S.dormant}>
              DORMANT — the pulse is reading this domain and there has been no
              mutation in the window. Rate 0 is the real measurement, not a gap.
            </div>
          ) : (
            <div style={S.rows}>
              {rows.slice(0, 8).map((r) => (
                <div key={r.journal_ref} style={S.row}>
                  <span style={S.rowWho}>{r.who}</span>
                  <span style={S.rowSubject}>{r.subject}</span>
                  <span style={S.rowLocus}>{r.locus}</span>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </Panel>
  );
}

// ── 3 · combined budget rollup ───────────────────────────────────────────────
export function BudgetRollup({ board }: { board: DomainBoard }) {
  const root = board[PANEL_ROOT.budget];
  return (
    <Panel title="Budget rollup" accent={MC.amber}
           status={root ? <Chip text="live" tone="live" /> : <Chip text="awaiting first write" tone="empty" />}>
      {root ? <pre style={S.pre}>{JSON.stringify(root, null, 2)}</pre> : <EmptyState what="budget data" />}
    </Panel>
  );
}

// ── 4 · mission-control agent roster (LIVE) ──────────────────────────────────
// Rendered from the registry projection. The identifier is the agent NAME; a job
// title would be presentation, and none is declared for these agents yet.
// THE FOUR ROSTER COLOURS. The rule lives in `rosterState.ts` and this is only
// its paint — a colour chosen in a JSX branch is how a fifth state appears
// without anyone deciding on it.
//
// EXPORTED, because the sidebar rail paints the same four states for the same
// agent. Two indicators disagreeing about one agent is worse than one being
// absent, so there is ONE table and ONE resolver behind both — see
// `AgentStateDot` below.
const ROSTER_DOT: Record<RosterDotState, React.CSSProperties> = {
  green:  { background: DOT.live, boxShadow: `0 0 6px ${DOT.live}` },
  orange: { background: DOT.dormant },
  red:    { background: DOT.failed },
  // HOLLOW, because it is not a state of the agent — it is the absence of an
  // answer, and it must not read as one of the three at a glance.
  grey:   { background: 'transparent', border: `1px solid ${DOT.idle}` },
};

/**
 * ONE DOT, FOUR STATES, TWO SURFACES.
 *
 * The Overview roster and the sidebar rail both show Alpha, and they must never
 * disagree: the state comes from `rosterState`, the colour comes from the table
 * above, and neither surface holds a copy of either.
 *
 * `boxSizing: 'border-box'` IS LOAD-BEARING. The unread state is a 1px BORDER
 * rather than a fill, and without this it measures 2px wider than every filled
 * dot beside it — which is exactly what the roster shipped with until the
 * browser harness measured it. jsdom has no layout and cannot see it.
 */
export function AgentStateDot(
  { state, size = 6, testId = 'agent-state-dot' }:
  { state: RosterDotState; size?: number; testId?: string },
) {
  return (
    <span
      data-testid={testId}
      data-roster-dot={state}
      style={{
        width: size, height: size, borderRadius: 999,
        boxSizing: 'border-box', flexShrink: 0, display: 'inline-block',
        ...ROSTER_DOT[state],
      }}
    />
  );
}

/**
 * ══ THE ROOT STATUS BANNER (owner ruling 2026-09-02) ═══════════════════════
 *
 * The strip above a root's panels. For eight of the nine roots it is what it
 * always was — the standing description, one `<div>`, byte-for-byte the markup
 * that shipped. For the root whose DECLARED owner is the harness CEO it also
 * carries that agent's state, because the page said the same thing whether the
 * agent was healthy or dead.
 *
 * ⭐⭐ TWO SLOTS, AND THE FIRST HAS A FIXED HEIGHT. The claim is one line, clipped
 * rather than wrapped, and the slot is rendered in ALL FOUR states — holding
 * nothing in the unread one. That is not decoration: it is what makes the strip
 * the SAME HEIGHT whatever it says, so the whole page below it does not move
 * when Alpha's state changes. A banner whose height changes with its content is
 * the `box-sizing` fault in a new place, and jsdom cannot see either.
 *
 * ⭐ AN EMPTY SLOT IS NOT A CLAIM. Where the health cannot be read there is no
 * dot and no word — green would be inferred, and orange is as much an assertion
 * as green. The description still renders: what the CEO IS stays true whether or
 * not it is running.
 *
 * THE DETAIL IS NOT ON THE STRIP. A status line answers; the panels below carry
 * the reasoning, and the producer's full text rides the hover title.
 */
export function RootNoteBanner({ note, health, stateful, now }: {
  note: string;
  health?: CeoHealth | null;
  /** Does this root's declared owner publish health? Passed by the surface from
   *  `ROLE_ROOT`, never tested for by name here. */
  stateful?: boolean;
  now?: number;
}) {
  if (!stateful) return <div style={S.rootNote}>{note}</div>;
  // ONE RESOLVER. The state is not decided here and cannot be: `ceoBannerState`
  // delegates to the same `ceoDotState` the rail and the roster call.
  const line = ceoBannerState(health, now);
  return (
    <div style={S.rootNote} data-testid="root-note-banner" data-banner-state={line.state}>
      <div style={S.bannerClaim} data-testid="banner-claim"
           title={line.detail || undefined}>
        {line.claim !== null && (
          <>
            <AgentStateDot state={line.state} size={7} testId="banner-dot" />
            <span style={S.bannerClaimText} data-testid="banner-claim-text">{line.claim}</span>
          </>
        )}
      </div>
      <div style={S.bannerBody} data-testid="banner-note">{note}</div>
    </div>
  );
}

/**
 * ══ THE RAIL'S ROOT DOT, THREE STATES ══════════════════════════════════════
 *
 * A sibling of `AgentStateDot`, and here for the same reason: the rule lives in
 * `rosterState`, the colour lives in one table, and neither is chosen in a JSX
 * branch — which is how a fourth state appears without anyone deciding on it.
 *
 * ⭐ `boxSizing: 'border-box'` IS LOAD-BEARING, and the rail's `S.dot` did not
 * have it. `unread` is a 1px BORDER rather than a fill, so without this it
 * measures 10px against the filled dots' 8 — the exact fault the Overview
 * roster shipped with until a browser measured it. jsdom has no layout.
 */
const ROOT_DOT: Record<RootDataState, React.CSSProperties> = {
  live:   { background: DOT.live },
  empty:  { background: DOT.idle },
  // HOLLOW, because it is not a state of the root — it is the absence of an
  // answer, and it must not read as either of the two that are.
  unread: { background: 'transparent', border: `1px solid ${DOT.idle}` },
};

export const ROOT_DOT_LABEL: Record<RootDataState, string> = {
  live: 'the panels this page reads have data',
  empty: 'nothing on the board for this page yet',
  unread: 'the board could not be read',
};

export function RootStateDot(
  { state, size = 8, style, testId = 'rail-root-dot' }:
  { state: RootDataState; size?: number; style?: React.CSSProperties; testId?: string },
) {
  return (
    <span
      data-testid={testId}
      data-root-state={state}
      title={ROOT_DOT_LABEL[state]}
      style={{
        width: size, height: size, borderRadius: 999,
        boxSizing: 'border-box', flexShrink: 0, display: 'inline-block',
        ...ROOT_DOT[state],
        ...style,
      }}
    />
  );
}

export function AgentRoster({ roster, ready, health }: {
  roster: StructureAgent[]; ready?: boolean; health?: CeoHealth | null;
}) {
  return (
    <Panel title="Agent roster" accent={MC.violet}
           status={<Chip text={ready === false ? 'reading…' : `live · ${roster.length}`}
                         tone={ready === false ? 'empty' : 'live'} />}>
      {roster.length === 0 ? (
        <div style={S.empty}>
          {ready === false ? 'reading…' : 'no agents registered for this domain'}
        </div>
      ) : (
        <div style={S.rosterGrid}>
          {roster.map((a) => {
            // ⭐ THIS LINE WAS `a.implementationState === 'live'` AND COULD NEVER
            // BE TRUE — the projection writes `LIVE_BUILT`, so every dot on this
            // panel was the idle grey and nothing said so. The same comparison
            // was fixed on the rail on 2026-08-31 and this twin was left; the
            // test is a normalised LIVE prefix now, in one place, so no single
            // spelling is load-bearing again.
            const dot = rosterDotState(a, health);
            return (
              <div key={a.id} style={S.rosterCell}>
                <span title={rosterDotLabel(a, health)} style={S.dotWrap}>
                  <AgentStateDot state={dot} testId="roster-dot" />
                </span>
                <span style={S.rosterBody}>
                  <span style={S.rosterName}>{a.name}</span>
                  {/* WHAT WAS READ, in a word. `implementationState` answered
                      "was this agent built" — never the owner's question, which
                      is whether it is working. */}
                  <span style={S.rosterState}>{rosterDotLabel(a, health)}</span>
                </span>
              </div>
            );
          })}
        </div>
      )}
    </Panel>
  );
}

// ── 5 · daily CEO report preview ─────────────────────────────────────────────
// THE WIRE WAS ALREADY HERE. This panel is handed `feed.board` ENTIRE — not the
// Overview root every other panel gets — so it has always been able to read the
// `ceo` root. What it did with it was dump the whole root through
// `JSON.stringify` into a <pre>. That was the unbuilt part.
//
// ── IT UPDATES WHEN A REPORT COMPOSES, NOT WHEN ANYONE LOOKS ───────────────
// `useDomainSurface` polls `/api/domain-board/GROWTH` every 30 s and
// `alpha_board.publish` writes the `ceo` root on every pulse, so a report
// composed at the 07:00 or 19:00 boundary reaches this panel within one pulse
// plus the poll. No manual step, and nothing here triggers a compose — a
// reporting surface that made the thing it reports on run would be the same
// class of fault as a verification panel fed by what it verifies.
//
// ⭐ AND THE EMPTY REPORT IS A REAL REPORT. The only one on disk is the 07:00
// boundary with `empty: True`, `content: ''` and `notes_total: 0` — the
// 19:00→07:00 window genuinely had no notes. `alpha_board` already turns that
// into the sentence "the window closed with no notes to report", and this
// renders it AS THE REPORT, with its stamp, rather than as a blank body or an
// error. A composed-and-empty window and a panel that failed to read are
// different facts and must not look alike.
export function CeoReportPreview({ board }: { board: DomainBoard }) {
  const root = board[PANEL_ROOT.ceo] as Record<string, unknown> | undefined;
  const daily = (root || {})['daily_reports'] as { reports?: unknown } | undefined;
  const reports = Array.isArray(daily?.reports)
    ? (daily!.reports as Record<string, string>[]) : [];
  // THE HEAD IS THE LATEST. `alpha_report.reports_index` sorts `composed_at`
  // descending, so this takes the newest without re-sorting it here — a second
  // ordering rule is how two surfaces come to disagree about which is current.
  const latest = reports.length > 0 ? reports[0] : null;
  const empty = latest ? String(latest.state || '') === 'empty' : false;

  return (
    <Panel title="Daily CEO report" accent={MC.blue}
           status={latest
             ? <Chip text={empty ? 'no notes in window' : String(latest.state || 'report')}
                     tone={empty ? 'empty' : 'live'} />
             : <Chip text="awaiting first write" tone="empty" />}>
      {!latest ? (
        // NOT READ vs NOTHING COMPOSED. With no `ceo` root at all there is
        // nothing to say; with a root but no reports, the harness has run and
        // has not reached a boundary yet, and that is worth saying.
        root
          ? <div style={S.empty}>
              no report composed yet — two are written per day, at the 07:00 and
              19:00 gates
            </div>
          : <EmptyState what="CEO report" />
      ) : (
        <div style={S.report}>
          <div style={S.reportHead}>
            <b style={S.reportDay}>{latest.day || '—'}</b>
            <span style={S.reportAt}>composed {latest.at || '—'}</span>
          </div>
          <p style={empty ? S.reportEmptyBody : S.reportBody}>
            {latest.summary || ''}
          </p>
          {reports.length > 1 && (
            <div style={S.reportMore}>
              {reports.length - 1} earlier report{reports.length === 2 ? '' : 's'} on the CEO tab
            </div>
          )}
        </div>
      )}
    </Panel>
  );
}

// ── 6 · telco rollup ─────────────────────────────────────────────────────────
export function TelcoRollup({ board }: { board: DomainBoard }) {
  const root = board[PANEL_ROOT.telco];
  return (
    <Panel title="Telco rollup" accent={MC.teal}
           status={root ? <Chip text="live" tone="live" /> : <Chip text="awaiting first write" tone="empty" />}>
      {root ? <pre style={S.pre}>{JSON.stringify(root, null, 2)}</pre> : <EmptyState what="telco data" />}
    </Panel>
  );
}

// ── 7 · API change watch ─────────────────────────────────────────────────────
export function ApiChangeWatch({ board }: { board: DomainBoard }) {
  const root = board[PANEL_ROOT.apiWatch];
  return (
    <Panel title="API change watch" accent={MC.violet}
           status={root ? <Chip text="live" tone="live" /> : <Chip text="awaiting first write" tone="empty" />}>
      {root ? <pre style={S.pre}>{JSON.stringify(root, null, 2)}</pre> : <EmptyState what="integration changes" />}
    </Panel>
  );
}

// ── a DECLARED panel ─────────────────────────────────────────────────────────
// The panel exists because the design declares it; its CONTENT comes from the
// board root's slot of the same key and is honestly empty until a write lands.
// Structure is the app, data is the API — this component is where that line sits.
//
// A TABLE ALWAYS SHOWS ITS HEADER. The columns are the design's and are true
// before any data exists; hiding them until a row arrives would hide the shape
// of the thing. The BODY is what waits.
/**
 * THE TENANT PLACEHOLDER. Owner ruling 2026-08-25: the surface is
 * tenant-parameterised and must stay so — VET‑01 is a SAMPLE in the design, not
 * a value, and hardcoding it would build seven applications where the ruling
 * says one. So a banner writes `{tenant}` and the renderer fills it from nav.
 *
 * WITH NO TENANT SELECTED IT READS THE EM DASH, the same as an unanswered
 * reading — the surface never invents a name to make a title look finished.
 *
 * This is the same substitution the admin sub-tab's LABEL already does inline
 * (MissionControl.tsx), moved somewhere both can mean it.
 */
export function fillTenant(text: string, tenant?: string | null): string {
  return text.replace(/\{tenant\}/g, tenant || '—');
}

export function DeclaredPanel({ spec, data, tenant }: {
  spec: PanelSpec; data: unknown; tenant?: string | null;
}) {
  const row = (data || {}) as Record<string, unknown>;
  const has = data !== undefined && data !== null
    && !(typeof data === 'object' && Object.keys(data as object).length === 0);
  const bodies = spec.body || [];
  // ── A SELF-FED PANEL IS NEVER "AWAITING FIRST WRITE" ────────────────────
  // The status chip reads the BOARD SLOT, and a self-fetching body does not
  // have one — nothing will ever write `ceo_activity`, because the panel holds
  // its own route. Left alone, the verification surface would have opened with
  // a chip saying it was waiting for a write that is never coming.
  //
  // AND IT DOES NOT GET A "live" CHIP INSTEAD, which would be the same lie in
  // the other direction the moment the fetch fails. It gets NO chip: the body
  // states its own liveness — `updated HH:MM:SS` when the read landed, and
  // `not_available — <error>` when it did not — and a second, weaker claim
  // beside a stronger one is worth less than the stronger one alone.
  const selfFed = bodies.some((b) => b.kind === 'activity');

  return (
    <Panel
      title={fillTenant(spec.title, tenant)}
      accent={MC.blue}
      note={spec.note}
      badge={spec.badge}
      panelKey={spec.key}
      status={selfFed ? undefined
        : has ? <Chip text="live" tone="live" /> : <Chip text="awaiting first write" tone="empty" />}
    >
      {bodies.length === 0 ? (
        <div style={S.empty}>
          no data written to <code style={S.code}>{spec.key}</code> yet — the panel is
          declared and reads this slot the moment its first write lands
        </div>
      ) : bodies.map((b, i) => (
        <Body key={i} body={b} data={row} panelKey={spec.key} tenant={tenant} />
      ))}
    </Panel>
  );
}

function Body({ body, data, panelKey, tenant }: {
  body: PanelBody; data: Record<string, unknown>; panelKey: string; tenant?: string | null;
}) {
  switch (body.kind) {
    case 'table':
      // EXTRACTED INTO A COMPONENT so it can hold the filter's state. A hook
      // called inside a switch arm would tie hook ORDER to `body.kind`; a
      // component of its own cannot, whatever the declaration says.
      return <TableBody body={body} data={data} panelKey={panelKey} />;
    case 'kpis':
      return (
        <div style={S.kpiGrid}>
          {body.tiles.map((t) => {
            const cell = readKpi(data[t.label]);
            return (
              <div key={t.label} style={S.kpiTile}>
                <div style={S.kpiLabel}>{t.label}</div>
                <div style={{ ...S.kpiValue, color: cell.has ? MC.teal : MC.dim }}>
                  {cell.has ? cell.value : '—'}
                </div>
                {/* THE SUB-LINE IS THE DESIGN'S STATIC DESCRIPTOR AND STAYS SO.
                    Only its TONE moves, and only when the board says which way —
                    see readKpi. Untoned is the degrade and also the common case. */}
                {t.sub && (
                  <div style={cell.direction
                    ? { ...S.kpiSub, color: KPI_DIRECTION[cell.direction] }
                    : S.kpiSub}>{t.sub}</div>
                )}
                {/* A STATIC TILE SAYS WHY (owner ruling 2026-09-17). The PRODUCER
                    names when what the tile counts last changed; this paints it and
                    derives nothing. Absent on the board = nothing rendered. */}
                {cell.since && <div style={S.kpiSince}>{cell.since}</div>}
              </div>
            );
          })}
        </div>
      );
    case 'metrics':
      return (
        <div style={S.metrics}>
          {body.rows.map((r) => {
            const v = data[r];
            const has = typeof v === 'number' || typeof v === 'string';
            return (
              <div key={r} style={S.metricRow}>
                <span style={S.metricName}>{r}</span>
                <b style={{ ...S.metricValue, color: has ? MC.text : MC.dim }}>
                  {has ? String(v) : '—'}
                </b>
              </div>
            );
          })}
        </div>
      );
    case 'checklist':
      // Doctrine text, not a measurement — it renders in full and waits on nothing.
      return (
        <ul style={S.checklist}>
          {body.items.map((it) => (
            <li key={it} style={S.checkItem}>
              <span style={S.checkTick}>✓</span>{it}
            </li>
          ))}
        </ul>
      );
    case 'pills':
      return (
        <div style={S.pillStack}>
          {body.items.map((it) => <span key={it} style={S.pill}>{it}</span>)}
        </div>
      );
    case 'map':
      // A REAL MAP, NOT A CAPTION ABOUT ONE. Zero pins is a map with nothing on
      // it — pannable and zoomable — never a line of text standing in for the
      // map itself. Pan and zoom are browser-side and reach nothing; see
      // DiscoveryMap.tsx for why no view listener exists.
      return <DiscoveryMap pins={data.pins} />;
    case 'cards': {
      // BOARD-FED RECORD CARDS. Absent, the DESIGN'S OWN empty state renders —
      // AS A CARD, which is how the design draws it, not as the generic empty
      // line. Transcribed into the spec, so it is the design's words and not
      // this renderer's.
      const cards = Array.isArray(data.cards) ? (data.cards as Record<string, string>[]) : [];
      if (cards.length === 0) return (
        <div style={S.recordCard}>
          <b style={S.recordName}>{body.empty.name}</b>
          <small style={S.recordDetail}>{body.empty.detail}</small>
        </div>
      );
      return (
        <div>
          {cards.map((c, i) => {
            // the tag is toned by the SAME entry point as every other value
            const tone = c.tag ? cellTone(panelKey, 'cards', c.tag) : null;
            return (
              <div key={i} style={S.recordCard}>
                <b style={S.recordName}>{c.name || '—'}</b>
                <small style={S.recordDetail}>{c.detail || ''}</small>
                {c.tag && (
                  <span style={{ ...PILL[tone || 'plain'], marginTop: 7, display: 'inline-block' }}>
                    {c.tag}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      );
    }
    case 'hours': {
      // ALL 24 HOURS EXIST BEFORE ANY PULSE FIRES. The board is the reporting
      // spine, so it is drawn in full and each node says which of the three
      // states it is in. A node with no entry is WAITING — not missing, and not
      // an error; the pulse for that hour simply has not fired yet.
      const byHour = (data.hours || {}) as Record<string, { title?: string; note?: string; state?: string }>;
      return (
        <div style={S.hourBoard}>
          {Array.from({ length: 24 }, (_, h) => {
            const key = `${String(h).padStart(2, '0')}:00`;
            const n = byHour[key];
            const state = n?.state || (n ? 'complete' : 'future');
            const tone = state === 'complete' ? MC.green : state === 'current' ? MC.teal : MC.dim;
            return (
              <div key={key} style={{ ...S.hourNode, borderColor: n ? tone : MC.line2,
                     background: state === 'current' ? MC.tealSoft : 'rgba(255,255,255,.02)' }}>
                <time style={{ ...S.hourTime, color: tone }}>{key}</time>
                <b style={S.hourTitle}>{n?.title || 'Waiting'}</b>
                <small style={S.hourNote}>{n?.note || 'pulse not fired'}</small>
              </div>
            );
          })}
        </div>
      );
    }
    // ── THE SHARED TIMELINE, WIDENED TO CARRY ITS SOURCE ────────────────────
    // OWNER RULING 2026-09-01. `surface.ts` badges `latest_ceo_notes`
    // "source-linked" and its note says the CEO reads calls, transcripts and
    // admin actions BEFORE writing — and NO PRODUCER COULD SATISFY THAT BADGE,
    // because this renderer read exactly {when, text}. A third key published
    // into `events[]` rendered nowhere.
    //
    // WHY IT IS NOT COSMETIC: on HOMEPAGE every note Rexy writes surfaces
    // against its source event, and that is how the owner catches drift and
    // hallucination in the note-taking — by reading the note against the thing
    // it claims to describe. A NOTE WITH NO VISIBLE SOURCE CANNOT BE CHECKED.
    //
    // ADDITIVE, AND IT DEGRADES TO THE OLD LINE. The meta row renders only when
    // the producer publishes at least one of the three; absent, this is byte
    // for byte what it was. It reads as HER panel already does —
    // `NotesPanel.tsx:55-60`: {who} then {KIND_LABEL[source_kind]} · {where}.
    //
    // INHERITED BY BOTH TIMELINES, DELIBERATELY: `latest_ceo_notes` (GROWTH ▸
    // CEO, the badged one) and `ceo_escalation_queue` (GROWTH ▸ Sales). The
    // escalation queue gains a slot it does not yet fill and renders unchanged
    // until its own producer opts in — which is the point of an additive slot
    // rather than a second renderer.
    case 'timeline': {
      const events = Array.isArray(data.events) ? (data.events as Record<string, string>[]) : [];
      // The label map is the ONE the rest of the OS uses — imported, not a
      // second copy, so `agent_dashboard` cannot come to read "dashboard" here
      // and something else three panels away. An unknown kind renders ITSELF
      // rather than being dropped: a source we cannot label is still a source.
      const kindWord = (k?: string) =>
        (k ? (ACTIVITY_KIND_LABEL as Record<string, string>)[k] || k : '');
      return events.length === 0
        ? <div style={S.empty}>no notes written yet — each hourly pulse appends one</div>
        : <div style={S.timeline}>{events.map((e, i) => {
            const kind = kindWord(e.source_kind);
            const trail = [kind, e.where].filter(Boolean).join(' · ');
            const hasMeta = Boolean(e.who || trail);
            return (
              <div key={i} style={S.event}>
                <b style={S.eventWhen}>{e.when || '—'}</b>
                {hasMeta ? (
                  <small style={S.eventMeta}>
                    {e.who ? <span style={S.eventWho}>{e.who}</span> : null}
                    {e.who && trail ? ' · ' : ''}
                    {trail}
                  </small>
                ) : null}
                <small style={S.eventText}>{e.text || ''}</small>
              </div>);
          })}</div>;
    }

    // ── THE ACTIVITY FEED — ALPHA'S, AND IT IS NOT BOARD-FED ────────────────
    // OWNER RULING 2026-09-01: activity, pulse, notes, reporting and dreamstate
    // are five separate things in a chain, each standing on its own. This panel
    // does NOT read `data` — it holds its own route, exactly as Rexy's does,
    // and that is the requirement rather than an exception to the pattern:
    //
    //     A PANEL THAT RENDERS FROM A BOARD WRITTEN BY THE THING BEING
    //     VERIFIED VERIFIES NOTHING.
    //
    // `alpha_board` publishes the `ceo` root; if this fed off that root, the
    // owner would be checking the harness against the harness's own account of
    // itself. It reads the mutation journal through Alpha's own lane instead.
    //
    // `framed={false}` because the `Panel` above already drew the card, the
    // title, the note and the badge.
    case 'activity':
      return <ActivityPanel lane={GROWTH_LANE} framed={false} testId="mc-activity" />;
    case 'reports': {
      const reports = Array.isArray(data.reports) ? (data.reports as Record<string, string>[]) : [];
      return reports.length === 0
        ? <div style={S.empty}>
            no reports compiled yet — two are written per day, at the 07:00 and 19:00
            gates, from that window's hourly notes
          </div>
        : <div style={S.reportGrid}>{reports.map((r, i) => (
            <div key={i} style={S.reportCard}>
              <h4 style={S.reportTitle}>{r.day || '—'}</h4>
              <p style={S.reportBody}>{r.summary || ''}</p>
              <div style={S.reportMeta}><span>{r.state || '—'}</span><span>{r.at || '—'}</span></div>
            </div>))}</div>;
    }
    case 'ledger': {
      const rows = Array.isArray(data.ledger) ? (data.ledger as Record<string, string>[]) : [];
      return rows.length === 0
        ? <div style={S.empty}>no events recorded in this window</div>
        : <div style={S.ledger}>{rows.map((r, i) => (
            <div key={i} style={S.ledgerRow}>
              <time style={S.ledgerTime}>{r.at || '—'}</time>
              <div style={S.ledgerBody}>
                <b style={S.ledgerName}>{r.event || '—'}</b>
                <span style={S.ledgerDetail}>{r.detail || ''}</span>
              </div>
              {/* THE SLOT IS NAMED `state`, and it goes through the SAME single
                  entry point every table cell uses — the axis is the slot, and a
                  column label is only what a table's slot is called. Sampled from
                  eight rows across the two ledger panels, so the vocabulary is
                  read rather than derived. Degrade is unchanged: a state this
                  panel never sampled renders its own text, exactly as today. */}
              <LedgerState panelKey={panelKey} value={r.state} />
            </div>))}</div>;
    }
    case 'callTiles':
      return (
        <div style={S.callShell}>
          {body.tiles.map((t) => {
            const v = data[t.label];
            const has = typeof v === 'number' || typeof v === 'string';
            return (
              <div key={t.label} style={S.callTile}>
                <small style={S.callTileLabel}>{t.label}</small>
                <b style={{ ...S.callTileValue, color: has ? MC.text : MC.dim }}>
                  {has ? String(v) : '—'}
                </b>
                {/* the design's own line, and the first of the four names the
                    tenant — covered by the same fill the banners use */}
                <span style={S.callTileSub}>{fillTenant(t.sub, tenant)}</span>
              </div>
            );
          })}
        </div>
      );
    case 'messages': {
      const rows = Array.isArray(data.messages) ? (data.messages as Record<string, string>[]) : [];
      return rows.length === 0
        ? <div style={S.empty}>no calls or messages in this window</div>
        : <div style={S.messageConsole}>{rows.map((m, i) => (
            <div key={i} style={S.messageRow}>
              <time style={S.messageTime}>{m.at || '—'}</time>
              <div style={S.messageBody}>
                <b style={S.messageTitle}>{fillTenant(m.title || '—', tenant)}</b>
                <small style={S.messageDetail}>{m.detail || ''}</small>
              </div>
              {/* THE SAME SINGLE ENTRY POINT. `sent` is PLAIN in this panel,
                  amber in stripe_payment_state and green in
                  transcript_to_admin_queue — three tones for one word, correct
                  by construction because the key is the panel. */}
              {m.state ? <MessageState panelKey={panelKey} state={m.state} /> : null}
            </div>))}</div>;
    }
    case 'stats':
      return (
        <div style={S.statShell}>
          {body.items.map((it) => {
            const v = data[it];
            const has = typeof v === 'number' || typeof v === 'string';
            return (
              <div key={it} style={S.statBlock}>
                <small style={S.statLabel}>{it}</small>
                <b style={{ ...S.statValue, color: has ? MC.text : MC.dim }}>{has ? String(v) : '—'}</b>
              </div>
            );
          })}
        </div>
      );
    case 'chips': {
      const health = (data.health || {}) as Record<string, string>;
      return (
        <div style={S.chipRibbon}>
          {body.items.map((it) => {
            const h = health[it];
            const tone = h === 'warn' ? MC.amber : h === 'down' ? MC.red : h ? MC.green : MC.dim;
            return (
              <span key={it} style={{ ...S.serviceChip, borderColor: tone }}>
                <span style={{ ...S.chipLed, background: tone, boxShadow: `0 0 7px ${tone}` }} />
                {it}
              </span>
            );
          })}
        </div>
      );
    }
    case 'registry': {
      const items = Array.isArray(data.connectors) ? (data.connectors as Record<string, string>[]) : [];
      return items.length === 0
        ? <div style={S.empty}>no connectors registered yet</div>
        : <div style={S.rosterGrid}>{items.map((c, i) => (
            <div key={i} style={S.rosterCell}>
              <span style={S.rosterBody}>
                <span style={S.rosterName}>{c.name || '—'}</span>
                <span style={S.rosterState}>{c.state || ''}</span>
              </span>
            </div>))}</div>;
    }
    case 'flow':
      return (
        <div style={S.flow}>
          {body.steps.map((st, i) => {
            const state = (data.steps as Record<string, string> | undefined)?.[st.label];
            const on = !!state;
            return (
              <React.Fragment key={st.label}>
                <div style={{ ...S.flowStep, borderColor: on ? MC.teal : MC.line2,
                              background: on ? MC.tealSoft : 'rgba(255,255,255,.02)' }}>
                  <b style={S.flowLabel}>{st.label}</b>
                  {st.sub && <small style={S.flowSub}>{st.sub}</small>}
                </div>
                {i < body.steps.length - 1 && <span style={S.flowArrow}>›</span>}
              </React.Fragment>
            );
          })}
        </div>
      );
    case 'funnel': {
      const vals = (data.stages || {}) as Record<string, number>;
      // The bar is scaled to the WIDEST stage present, never to a fixed maximum —
      // a fixed one would draw a full bar for an empty funnel.
      const peak = Math.max(1, ...body.stages.map((st) => Number(vals[st]) || 0));
      return (
        <div style={S.funnel}>
          {body.stages.map((st) => {
            const v = vals[st];
            const has = typeof v === 'number';
            return (
              <div key={st} style={S.funnelRow}>
                <span style={S.funnelName}>{st}</span>
                <div style={S.funnelTrack}>
                  <div style={{ ...S.funnelBar, width: has ? `${(v / peak) * 100}%` : 0 }} />
                </div>
                <b style={{ ...S.funnelValue, color: has ? MC.text : MC.dim }}>
                  {has ? String(v) : '—'}
                </b>
              </div>
            );
          })}
        </div>
      );
    }
    case 'tasks': {
      const tasks = Array.isArray(data.tasks) ? (data.tasks as Record<string, string>[]) : [];
      return tasks.length === 0
        ? <div style={S.empty}>no calls scheduled in this window</div>
        : <div style={S.taskList}>{tasks.map((t, i) => {
            const inner = (
              <>
                <span style={{ ...S.taskBox, borderColor: t.state === 'done' ? MC.green : MC.line2,
                               color: t.state === 'done' ? MC.green : 'transparent' }}>✓</span>
                <span style={S.taskBody}>
                  <b style={S.taskWhen}>{t.when || '—'}{t.who ? ` · ${t.who}` : ''}</b>
                  {/* THE FIT FLAG ON EVERY ROW (owner ruling 2026-10-06) — the
                      producer's words, shown; the list is already in Fit order. */}
                  {t.fit && <small style={S.taskFit} data-testid="call-row-fit">{t.fit}</small>}
                  <small style={S.taskNote}>{t.note || ''}</small>
                </span>
                <em style={S.taskState}>{t.state || 'open'}</em>
              </>
            );
            // A ROW CARRYING ITS OWN IDS IS A CALL THE OWNER CAN ANSWER. Pressing
            // it selects that company and hands the call gate THIS row — the
            // producer's `sales_task_id` / `call_id`, never minted
            // (`lib/deltaCallRow`). A done row, or a row with no ids, renders
            // exactly as before.
            const call = callRowOf(t);
            return call
              ? <button key={i} type="button" onClick={() => pressCallRow(call)}
                        style={{ ...S.task, ...S.taskPress }}>{inner}</button>
              : <div key={i} style={S.task}>{inner}</div>;
          })}</div>;
    }
    case 'script': {
      // THE SCRIPT SHEET IS A LIGHT PAGE INSIDE A DARK SURFACE, exactly as the
      // design declares it (#f7f9fb on #13293f). This is the one place the dark
      // base is deliberately not carried through: it is a printed document the
      // owner reads on a call, not a panel. The palette is the design's own.
      //
      // ── THE SHEET IS A TREE (owner ruling 2026-09-16) ────────────────────
      // Lines in order, a branch chip ON the line that triggers it, and what to
      // listen for BESIDE rather than below — the owner is mid-call when he
      // reads this, and a column under the fold is a column he will not look
      // at. `heading` is the line's own id, which is what a branch returns to.
      //
      // A VALUE IS EITHER WORDS OR WORDS-WITH-A-CITATION, and the producer has
      // already reduced both to `say` plus a `sourced` flag. THE CITATION NEVER
      // REACHES THIS FILE — it is an internal address and §10.14 forbids
      // narrating one to the owner.
      const blocks = Array.isArray(data.script) ? (data.script as any[]) : [];
      const listen = Array.isArray((data as any).listen_for) ? (data as any).listen_for as string[] : [];
      // THE FIT FLAG ON THE SCRIPT PAGE (owner ruling 2026-10-06), the producer's words.
      const fit = typeof (data as any).fit === 'string' && (data as any).fit ? (data as any).fit as string : null;
      return (
        <ScriptSheet blocks={blocks} listen={listen} fit={fit} />
      );
    }
    case 'questions':
      return (
        <div style={S.questionList}>
          {body.items.map((q, i) => (
            <div key={q.q} style={S.question}>
              {/* THE DESIGN NUMBERS ITS QUESTIONS — `.question:before` with
                  `counter-increment: q`, in a 34px column ahead of the body.
                  DERIVED FROM POSITION, so nothing new is declared and a
                  question added or removed renumbers itself. */}
              <span style={S.questionNo}>{i + 1}</span>
              <span style={S.questionBody}>
                <b style={S.questionText}>{q.q}</b>
                <small style={S.questionTests}>{q.tests}</small>
              </span>
              {/* THE TAG'S TEXT IS DECLARED; ITS TONE IS NOT. The slot is `tag`
                  and it goes through the same cellTone every table cell and
                  ledger row uses, so there is still ONE place that says how a
                  value is toned. Degrade is unchanged — a tag this panel never
                  sampled renders its own text, unstyled, rather than vanishing. */}
              <QuestionTag panelKey={panelKey} value={q.tag} />
            </div>
          ))}
        </div>
      );
    case 'workbook':
      return <Workbook sheets={body.sheets} data={data} panelKey={panelKey} />;
    case 'roleBanner':
      // THE DESIGN'S OWN WORDS. The title and the description say what this
      // surface IS — standing text, never a reading. Only a pill carrying a
      // `data` key is a measurement, and it shows '—' until the board answers.
      return (
        <div style={S.roleBanner}>
          <div>
            <b style={S.roleBannerTitle}>{fillTenant(body.title, tenant)}</b>
            <small style={S.roleBannerText}>{fillTenant(body.text, tenant)}</small>
          </div>
          <div style={S.roleBannerRight}>
            {body.pills.map((pl, i) => {
              const raw = pl.data ? data[pl.data] : undefined;
              const has = typeof raw === 'string' || typeof raw === 'number';
              // THE DESIGN SHOWS BOTH ORDERS and the sample governs: "8 services
              // healthy" leads with its reading, "last sync 10:18" trails. The
              // em dash takes the reading's place until the board answers, so an
              // empty pill still reads in the design's own word order.
              const shown = has ? String(raw) : '—';
              const text = pl.data
                ? (pl.valueFirst
                    ? `${shown}${pl.label ? ' ' + pl.label : ''}`
                    : `${pl.label ? pl.label + ' ' : ''}${shown}`)
                : (pl.label || '');
              // THE TONE COMES FROM THE `banner` SLOT, keyed by the pill's LABEL
              // — not from the pill, which used to carry it inline. Keyed by the
              // label rather than the rendered text because a `data` pill's text
              // contains a board reading and would never match a sampled word.
              // Unsampled renders as the untoned pill, as everywhere else.
              // THE TONE IS KEYED BY THE DECLARED LABEL, before the tenant is
              // filled in — a pill reading "{tenant}-A" is one vocabulary entry,
              // not one per tenant.
              const vis = cellTone(panelKey, 'banner', pl.label || '');
              return (
                <span key={i} style={PILL[vis || 'plain']}>
                  {fillTenant(text, tenant)}
                </span>
              );
            })}
          </div>
        </div>
      );
    case 'slides': {
      const slides = Array.isArray(data.slides) ? (data.slides as Record<string, string>[]) : [];
      return (
        <div style={S.slideGrid}>
          {Array.from({ length: body.count }, (_, i) => {
            const sl = slides[i];
            return (
              <div key={i} style={S.slideThumb}>
                <span style={S.slideNo}>{i + 1}</span>
                <b style={S.slideTitle}>{sl?.title || 'untitled slide'}</b>
                {sl?.note && <small style={S.slideNote}>{sl.note}</small>}
              </div>
            );
          })}
        </div>
      );
    }
    case 'suggestions': {
      const items = Array.isArray(data.suggestions) ? (data.suggestions as Record<string, string>[]) : [];
      return items.length === 0
        ? <div style={S.empty}>
            no suggestions — the co-pilot proposes; it never sends or presents
          </div>
        : <div style={S.suggestions}>{items.map((it, i) => (
            <div key={i} style={S.suggestion}>
              <b style={S.suggestionTitle}>{it.title || '—'}</b>
              <p style={S.suggestionBody}>{it.text || ''}</p>
            </div>))}</div>;
    }
    case 'inbox': {
      const mail = Array.isArray(data.messages) ? (data.messages as Record<string, string>[]) : [];
      return (
        <div style={S.inboxShell}>
          {mail.length === 0
            ? <div style={S.empty}>inbox empty — drafts and replies appear here; sends stay approval-gated</div>
            : mail.map((m, i) => (
                <div key={i} style={S.mailRow}>
                  <b style={S.mailFrom}>{m.from || '—'}</b>
                  <span style={S.mailSubject}>{m.subject || ''}</span>
                  <span style={S.mailState}>{m.state || ''}</span>
                </div>))}
        </div>
      );
    }
    case 'calendar': {
      const days = Array.isArray(data.days) ? (data.days as Record<string, unknown>[]) : [];
      return (
        <div style={S.calendarShell}>
          {days.length === 0
            ? <div style={S.empty}>no Sales entries scheduled — receptionist booking calendars stay separate</div>
            : days.map((d, i) => (
                <div key={i} style={S.calDay}>
                  <b style={S.calDayName}>{String(d.day ?? '—')}</b>
                  <span style={S.calEntries}>{String(d.entries ?? '')}</span>
                </div>))}
        </div>
      );
    }
    case 'volume': {
      const vals = (data.volume || {}) as Record<string, number>;
      // Scaled to the busiest hour PRESENT. A fixed maximum would draw a full
      // chart for an empty day; an empty day draws a flat baseline instead.
      const peak = Math.max(1, ...body.hours.map((h) => Number(vals[h]) || 0));
      return (
        <div style={S.volume}>
          {body.hours.map((h) => {
            const v = Number(vals[h]) || 0;
            return (
              <div key={h} style={S.volCol}>
                <div style={S.volTrack}>
                  <div style={{ ...S.volBar, height: v === 0 ? 2 : `${(v / peak) * 100}%`,
                                background: v === 0 ? MC.dim : MC.teal }} />
                </div>
                <small style={S.volLabel}>{h}</small>
              </div>
            );
          })}
        </div>
      );
    }
    case 'transcripts': {
      const calls = Array.isArray(data.calls) ? (data.calls as Record<string, string>[]) : [];
      return calls.length === 0
        ? <div style={S.empty}>no calls in this window — each completed call appends its transcript</div>
        : <div style={S.transcripts}>{calls.map((c, i) => (
            <details key={i} style={S.transcript}>
              <summary style={S.transcriptHead}>
                <b>{c.ref || '—'}</b>
                <span style={S.transcriptSub}>{c.summary || ''}</span>
                <span style={S.transcriptState}>{c.state || ''}</span>
              </summary>
              <div style={S.transcriptBody}>{c.body || ''}</div>
            </details>))}</div>;
    }
    case 'plugins': {
      const items = Array.isArray(data.plugins) ? (data.plugins as Record<string, string>[]) : [];
      return items.length === 0
        ? <div style={S.empty}>no connectors registered — this registry sits below the operational panels, never above them</div>
        : <div style={S.pluginGrid}>{items.map((pl, i) => {
            const tone = pl.health === 'warn' ? MC.amber : pl.health === 'down' ? MC.red : MC.green;
            return (
              <div key={i} style={S.pluginCard}>
                <span style={S.pluginHead}>
                  <span style={{ ...S.dot, background: tone, boxShadow: `0 0 7px ${tone}` }} />
                  <b style={S.pluginName}>{pl.name || '—'}</b>
                </span>
                <span style={S.pluginMeta}>{pl.detail || ''}</span>
              </div>);
          })}</div>;
    }
    case 'matrix': {
      // Named groups down the side, the four agent roles across. The COLUMNS are
      // the ownership vocabulary and are true before any cell is filled.
      const groups = Array.isArray(data.groups) ? (data.groups as Record<string, unknown>[]) : [];
      return (
        <div style={S.tableWrap}>
          <table style={S.table}>
            <thead>
              <tr>
                <th style={S.th}>&nbsp;</th>
                {body.columns.map((c) => <th key={c} style={S.th}>{c}</th>)}
              </tr>
            </thead>
            <tbody>
              {groups.length === 0 ? (
                <tr><td style={S.tdEmpty} colSpan={body.columns.length + 1}>
                  no ownership rows yet — the four agent columns are the shape
                </td></tr>
              ) : groups.map((g, i) => (
                <tr key={i}>
                  <td style={{ ...S.td, color: MC.muted, fontWeight: 700 }}>{String(g.group ?? '—')}</td>
                  {body.columns.map((c) => {
                    const raw = g[c];
                    const text = String(raw ?? '—');
                    const tone = cellTone(panelKey, c, raw);
                    return (
                      <td key={c} style={S.td}>
                        {tone ? <span style={PILL[tone]}>{text}</span> : text}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }
    case 'note':
      return <div style={S.noteBox}>{body.text}</div>;
    default:
      return null;
  }
}

/** The six-sheet workbook. Its sheet tabs are the design's own, and each sheet
 *  carries its own column set — a tab is a VIEW onto the workbook, never a second
 *  owner of the state. */
// ─────────────────────────────────────────────────────────────────────────────
// THE SALES INTELLIGENCE WORKBOOK SHELL.
//
// IT MUST READ AS EXCEL, NOT AS A PANEL. Toolbar, formula row, sheet tabs, the
// light sheet with its sticky row-number gutter, and a status bar — the design's
// own structure, sampled in workbook.ts, never approximated.
//
// STRUCTURE PRESENT, DATA ABSENT — exactly as Regions & Maps shipped. The
// backend does not exist: every sheet renders its full column header row and no
// rows, the counts read '—', and nothing here claims otherwise.
//
// ONE RENDERER. Marketing's tab and Sales' tab are the same declaration through
// this one component, because they are the same file.
// ─────────────────────────────────────────────────────────────────────────────
// ── THE KPI TILE'S DIRECTION — BOARD DATA, NOT A LOOKUP ─────────────────────
// Owner ruling 2026-08-25: THE TONE TRACKS THE READING, NOT THE TILE. 91.7%
// healthy is green; 40% would not be. A tile that is permanently "up" is a tile
// whose tone is decoration rather than measurement, and the point of a KPI is
// that it moves. So `direction` ARRIVES ON THE BOARD beside the value and is
// never declared in the spec.
//
// IT IS ALSO NOT A VOCABULARY, and deliberately does not go through cellTone.
// There is nothing to look up: the board states the direction outright. A word
// map could not do this job anyway — presentation.ts already carries the rule
// that measurement-shaped text only ever matches the design's own sample, and
// "6 need owner input" and "91.7% live health" both open with a number and mean
// opposite things. It is editorial, not parseable.
//
// THE BOARD CONTRACT IS NOT BUILT HERE. This is the renderer's half only: it
// accepts { value, direction } if that is what arrives, and a bare value — which
// is all the board sends today — renders exactly as it does now.

/**
 * A DECLARED TABLE, PLUS THE TWO THINGS A DECLARATION MAY ADD TO ONE.
 *
 * ── `lanes` — THE TWO-STAGE FILTER (owner ruling 2026-08-28) ───────────────
 * REGION FIRST, SECTOR SECOND, and the sector list is computed against the
 * chosen region so a region with no mechanics does not offer Mechanics. The
 * options are DERIVED FROM THE ROWS: there is no sector list and no state list
 * anywhere on this side. That is not a convenience — `server/growth_lane.SECTORS`
 * is Forge's declared vocabulary and NO ROUTE PUBLISHES IT, so a list here would
 * be a sixth copy whose byte match with his is luck. Derived options cannot
 * drift from data they are read out of.
 *
 * DEFAULT IS EVERYTHING. A filter is a narrowing, never a starting state, so
 * both axes begin at LANE_ALL and LANE_ALL matches everything.
 *
 * IT MEASURES WHETHER IT CAN FILTER. `laneFilterable` asks the rows in hand.
 * TODAY NO ROW OF ANY OF THESE TABLES CARRIES A LANE — counted on the live
 * board: 108, 108, 95 rows, none with a region or sector. So the controls render
 * disabled with the reason and the table is untouched. It lights up by itself
 * when the producer emits the keys, WITH NO EDIT HERE.
 *
 * ── `action` — THE ONLY INTERACTIVE ELEMENT A TABLE MAY CARRY ──────────────
 * On the ready row and nowhere else. Which cell and which flag are the
 * declaration's, and the flag is THE PRODUCER'S OWN FIELD — the renderer never
 * decides which row is ready. It is inert because the surface is GET-only and
 * there is no endpoint behind it; a live-looking button that posts nowhere is
 * worse than one that says so, which is the idiom the workbook toolbar already
 * sets on this page.
 */
function TableBody({ body, data, panelKey }: {
  body: Extract<PanelBody, { kind: 'table' }>;
  data: Record<string, unknown>;
  panelKey: string;
}) {
  const rows = Array.isArray(data.rows) ? (data.rows as unknown[]) : [];

  const [region, setRegion] = useState<string>(LANE_ALL);
  const [sector, setSector] = useState<string>(LANE_ALL);

  const filterable = !!body.lanes && laneFilterable(rows);
  const regions = useMemo(
    () => (body.lanes ? regionOptions(rows) : []), [body.lanes, rows]);
  const sectors = useMemo(
    () => (body.lanes ? sectorOptions(rows, region) : []), [body.lanes, rows, region]);

  // A SELECTION THAT HAS GONE OUT OF SCOPE IS NOT A FILTER, IT IS AN EMPTY
  // TABLE. Changing region can leave the chosen sector absent from the new one;
  // the effective sector falls back to everything for that render and the
  // control displays what is actually APPLIED, so the caption and the rows can
  // never disagree. DERIVED — no setState during render, nothing here mutates.
  const liveSector = sectors.indexOf(sector) >= 0 ? sector : LANE_ALL;

  const shown = filterable ? applyLane(rows, region, liveSector) : rows;

  return (
    <div style={S.tableWrap}>
      {body.lanes && (
        <LaneFilter
          region={region}
          sector={liveSector}
          regions={regions}
          sectors={sectors}
          filterable={filterable}
          onRegion={setRegion}
          onSector={setSector}
        />
      )}
      <table style={S.table}>
        <thead>
          <tr>{body.columns.map((c) => <th key={c} style={S.th}>{c}</th>)}</tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td style={S.tdEmpty} colSpan={body.columns.length}>
                no rows yet — the columns are the shape, and they fill from{' '}
                <code style={S.code}>{panelKey}.rows</code>
              </td>
            </tr>
          ) : shown.length === 0 ? (
            // AN HONEST EMPTY STATE. The rows exist; this combination holds none
            // of them. It says so, with both halves of the combination and the
            // count it is a subset of — never a blank table, and never a silent
            // fall back to unfiltered.
            <tr>
              <td style={S.tdEmpty} colSpan={body.columns.length}>
                no companies in {region === LANE_ALL ? 'any region' : region}
                {' · '}{liveSector === LANE_ALL ? 'any sector' : liveSector} —{' '}
                {rows.length} row{rows.length === 1 ? '' : 's'} on this table, none
                in this combination
              </td>
            </tr>
          ) : shown.map((r, i) => (
            <tr key={i}>
              {body.columns.map((c, ci) => {
                // Read by COLUMN NAME. A row missing a column shows a dash
                // rather than shifting the other cells left.
                const cell = readCell((r as Record<string, unknown>)[c]);
                const raw = cell.value;
                const text = String(raw ?? '—');
                const tone = cellTone(panelKey, c, raw);
                // THE ROW EARNS THE BUTTON, and the producer decides that. `own`
                // because the flag arrives from the network on a row keyed by
                // board strings, and `=== true` because a value we did not write
                // is a value we do not read as consent — the same refusal the
                // loop toggle already makes on the other side.
                const isAction = !!body.action && c === body.action.column
                  && own(r as Record<string, unknown>, body.action.when) === true;
                return (
                  <td key={c} style={body.backend && ci === 0 ? { ...S.td, ...S.tdFirst } : S.td}>
                    {isAction ? (
                      <button
                        type="button"
                        disabled
                        style={{ ...S.rowAction, ...(tone ? PILL[tone] : null),
                                 cursor: 'not-allowed', opacity: 0.8 }}
                        title="no endpoint exists yet — this surface is GET-only, so this action has nowhere to go"
                      >
                        {text}
                      </button>
                    ) : tone ? <span style={PILL[tone]}>{text}</span> : text}
                    {cell.sub && <span style={S.cellSub}>{cell.sub}</span>}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const KPI_DIRECTION: Record<string, string> = { up: MC.green, down: MC.red };

/**
 * Read a KPI cell. EITHER a bare value (every cell today) OR { value, direction }.
 *
 * An unrecognised direction is DROPPED, not rendered and not guessed — the same
 * degrade rule as everywhere else, and it keeps a typo on the board from
 * inventing a colour. A shaped object with no usable value still reads '—', so a
 * direction can never smuggle a tile into looking populated.
 */
export function readKpi(raw: unknown): {
  has: boolean; value: string; direction: string | null; since: string | null;
} {
  const scalar = (v: unknown) =>
    typeof v === 'number' || typeof v === 'string'
      ? { has: true, value: String(v) } : { has: false, value: '' };
  if (raw && typeof raw === 'object' && !Array.isArray(raw)
      && Object.prototype.hasOwnProperty.call(raw, 'value')) {
    const o = raw as { value: unknown; direction?: unknown; since?: unknown };
    // `in` WOULD WALK THE PROTOTYPE CHAIN, so a board sending direction
    // "toString" or "constructor" would pass the guard and hand a FUNCTION to
    // the style as a colour. Own-property only. (Found by the mutation round:
    // the fault it exposed was in this line, not in the bar.)
    const dir = typeof o.direction === 'string'
      && Object.prototype.hasOwnProperty.call(KPI_DIRECTION, o.direction)
      ? o.direction : null;
    // `since` IS A SENTENCE OR IT IS NOTHING — own property, a non-blank string.
    const since = Object.prototype.hasOwnProperty.call(o, 'since')
      && typeof o.since === 'string' && o.since.trim() ? o.since : null;
    return { ...scalar(o.value), direction: dir, since };
  }
  return { ...scalar(raw), direction: null, since: null };
}

/**
 * READ A TABLE CELL. A bare value — which is every cell the board sends today —
 * or { value, sub }, the design's second line inside the cell. Identical in shape
 * and in degrade to the KPI cell's { value, direction }, and additive: nothing
 * about a bare cell changes.
 *
 * Own-property throughout, per the sweep: a board object is untrusted input.
 */
function readCell(raw: unknown): { value: unknown; sub: string | null } {
  if (raw && typeof raw === 'object' && !Array.isArray(raw)
      && Object.prototype.hasOwnProperty.call(raw, 'value')) {
    const o = raw as { value: unknown; sub?: unknown };
    return { value: o.value, sub: typeof o.sub === 'string' && o.sub ? o.sub : null };
  }
  return { value: raw, sub: null };
}

/** A message row's state, through the one entry point. */
function MessageState({ panelKey, state }: { panelKey: string; state: string }) {
  const tone = cellTone(panelKey, 'state', state);
  return <span style={PILL[tone || 'plain']}>{state}</span>;
}

/** A question's category tag, on the right of its row as the design places it.
 *  `flexShrink` is kept in both branches so a toned tag and an untoned one hold
 *  the same column. */

/** THE SCRIPT SHEET — the declared `cold_call_script` panel's body.
 *
 *  It holds ONE piece of state: which branch is open. Everything else is the
 *  producer's, and a panel that decided anything about a script would be a
 *  second authority on what the owner says out loud. */
function ScriptSheet({ blocks, listen, fit = null }: { blocks: any[]; listen: string[]; fit?: string | null }) {
  const [open, setOpen] = React.useState<string | null>(null);
  // THE OWNER'S DIRECTIONS, WHERE HIS EYE IS (`lib/scriptDirections`): "Line N —"
  // beside line N, "If they say …" inside that branch. A copy is placed; the
  // aside below keeps every entry, and one that cannot be placed stays there.
  const placed = placeDirections(listen);
  if (blocks.length === 0) {
    return (
      <div style={S.scriptSheet}>
        <div style={S.scriptEmpty}>
          no script written yet — the sheet is the owner&apos;s talk track and fills
          when the writer runs for the selected company
        </div>
      </div>
    );
  }
  return (
    <div style={S.scriptSheet}>
      {fit && <b style={S.scriptFit} data-testid="script-fit">{fit}</b>}
      <div style={S.scriptBody}>
        <div style={S.scriptLines}>
          {blocks.map((b, i) => (
            <div key={i} style={S.scriptBlock}>
              {b.heading && <b style={S.scriptHeading}>{b.heading}</b>}
              {b.say && <p style={S.scriptSay}>{b.say}</p>}
              {directionsForLine(placed, b.heading).map((d, di) => (
                <p key={'d' + di} style={S.scriptDirection}>{d}</p>
              ))}
              {/* THE OLD SHAPE STILL RENDERS. A block carrying `listen` is the
                  contract this panel had before the tree, and a producer that
                  still sends one is not broken by the new one. */}
              {b.listen && <p style={S.scriptListen}><b>Listen for:</b> {b.listen}</p>}
              {Array.isArray(b.branches) && b.branches.length > 0 && (
                <div style={S.scriptChips}>
                  {b.branches.map((br: any) => (
                    <button key={br.branch_id} type="button"
                            onClick={() => setOpen(open === br.branch_id ? null : br.branch_id)}
                            style={open === br.branch_id ? S.scriptChipOpen : S.scriptChip}>
                      {br.objection_class || br.branch_id}
                    </button>
                  ))}
                </div>
              )}
              {Array.isArray(b.branches) && b.branches.filter((br: any) => br.branch_id === open)
                .map((br: any) => (
                <div key={'o' + br.branch_id} style={S.scriptBranch}>
                  {br.trigger && <div style={S.scriptTrigger}>{br.trigger}</div>}
                  {directionsForBranch(placed, br.trigger).map((d, di) => (
                    <div key={'d' + di} style={S.scriptBranchDirection}>{d}</div>
                  ))}
                  {(br.moves || []).map((m: any, mi: number) => (
                    <div key={mi} style={S.scriptMove}>
                      <span style={S.scriptMoveName}>{m.move}</span>
                      <span style={S.scriptMoveSay}>{m.say}</span>
                    </div>
                  ))}
                  {br.returns_to && <div style={S.scriptReturn}>back to {br.returns_to}</div>}
                </div>
              ))}
            </div>
          ))}
        </div>
        {listen.length > 0 && (
          <div style={S.scriptAside}>
            <div style={S.scriptAsideTitle}>LISTEN FOR</div>
            {listen.map((l, i) => <div key={i} style={S.scriptAsideItem}>{l}</div>)}
          </div>
        )}
      </div>
    </div>
  );
}

function QuestionTag({ panelKey, value }: { panelKey: string; value: string }) {
  const tone = cellTone(panelKey, 'tag', value);
  return tone
    ? <span style={{ ...PILL[tone], flexShrink: 0 }}>{value}</span>
    : <span style={{ ...S.questionTests, flexShrink: 0 }}>{value}</span>;
}

/** The ledger row's trailing state. A pill when this panel sampled the word, and
 *  the plain text it renders today when it did not — the ledger may tone a state,
 *  it may never swallow one. `flexShrink` is kept in both branches so a toned row
 *  and an untoned row lay out identically. */
function LedgerState({ panelKey, value }: { panelKey: string; value?: string }) {
  const text = value || '—';
  const tone = cellTone(panelKey, 'state', value);
  return tone
    ? <span style={{ ...PILL[tone], flexShrink: 0 }}>{text}</span>
    : <span style={S.ledgerState}>{text}</span>;
}

function Workbook({ sheets, data, panelKey }: {
  sheets: { key: string; label: string; columns: string[] }[];
  data: Record<string, unknown>;
  panelKey: string;
}) {
  const [active, setActive] = useState(sheets[0]?.key);
  const sheet = sheets.find((x) => x.key === active) || sheets[0];
  const all = (data.sheets || {}) as Record<string, unknown>;
  const rows = Array.isArray(all[sheet.key]) ? (all[sheet.key] as Record<string, unknown>[]) : [];
  const status = (data.status || {}) as Record<string, unknown>;

  return (
    <div style={S.workbookShell}>

      {/* TOOLBAR — the file it is, and where it lives. */}
      <div style={S.wbToolbar}>
        <div style={S.wbFile}>
          <span style={S.wbIcon}>{WORKBOOK_FILE.icon}</span>
          <div>
            <b style={S.wbFileName}>{WORKBOOK_FILE.name}</b>
            <small style={S.wbFilePath}>{WORKBOOK_FILE.path}</small>
          </div>
        </div>
        <div style={S.wbActions}>
          {WORKBOOK_ACTIONS.map((a) => (
            // DISABLED, AND VISIBLY SO. Each is an action that would go to the box,
            // and the workbook backend does not exist — so there is no endpoint for
            // any of them. They render because the design names them as part of what
            // makes this read as Excel; they are inert because a live-looking button
            // that posts nowhere is worse than an absent one. No handler is attached.
            <button key={a} type="button" disabled style={S.wbAction}
              title="the workbook backend does not exist yet — this action has nowhere to go">
              {a}
            </button>
          ))}
        </div>
      </div>

      {/* FORMULA ROW — the workbook's scoring rule as the design states it.
          STRUCTURE, NOT A COMPUTATION: nothing on this surface evaluates it. */}
      <div style={S.wbFormulaRow}>
        <span style={S.wbNameBox}>{WORKBOOK_FORMULA.nameBox}</span>
        <span style={S.wbFx}>{WORKBOOK_FORMULA.fx}</span>
        <span style={S.wbFormula} title={WORKBOOK_FORMULA.text}>{WORKBOOK_FORMULA.text}</span>
      </div>

      {/* SHEET TABS — all six exist before any backend answers. */}
      <div style={S.sheetTabs}>
        {sheets.map((sh) => {
          const on = sh.key === sheet.key;
          return (
            <button key={sh.key} type="button" onClick={() => setActive(sh.key)}
              style={{ ...S.sheetTab,
                background: on ? WB.tabActiveBg : WB.tabBg,
                color: on ? WB.tabActiveText : WB.tabText }}>
              {sh.label}
            </button>
          );
        })}
      </div>

      {/* THE SHEET. Light on purpose — see workbook.ts. */}
      <div style={S.sheetBody}>
        <table style={S.sheetTable}>
          <thead>
            <tr>
              {/* the row-number gutter's own corner */}
              <th style={{ ...S.sheetTh, ...S.rowHeadTh }} />
              {sheet.columns.map((c) => <th key={c} style={S.sheetTh}>{c}</th>)}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td style={{ ...S.sheetTd, ...S.rowHead }} />
                <td style={S.sheetEmpty} colSpan={sheet.columns.length}>
                  sheet empty — the backend writes this workbook; the columns are its shape
                </td>
              </tr>
            ) : rows.map((r, i) => (
              <tr key={i}>
                <td style={{ ...S.sheetTd, ...S.rowHead }}>{i + 1}</td>
                {sheet.columns.map((c) => {
                  // TWO DIFFERENT THINGS, AND THEY ARE NOT INTERCHANGEABLE.
                  // A SCORE column is styled as an Excel cell — the design fills the
                  // cell itself (#dff7e9, weight 900), the way a spreadsheet does.
                  // A STATE column carries a PILL, the same sampled vocabulary the
                  // rest of the reader uses. A column that is neither renders plain.
                  // A COLUMN FACT (owner ruling 2026-09-29): a value the same for every
                  // row — every company is the owner's; crowding is "—" — is sent once as
                  // `column_defaults` and fills any row that does not carry its own.
                  const defaults = data.column_defaults as Record<string, unknown> | undefined;
                  const raw = r[c] !== undefined ? r[c] : defaults?.[c];
                  const text = String(raw ?? '');
                  const tone = SCORE_COLUMNS.has(c) ? null : cellTone(panelKey, c, raw);
                  // THE CELL'S REASON OR SOURCE, ON HOVER (owner ruling 2026-09-29):
                  // Next action names the record it came from (`<column> · source` on the
                  // row); Crowding reads "—" and says why ONCE, as a column note
                  // (`column_notes`), not 258 times on the rows.
                  const src = r[c + ' · source'];
                  const note = (data.column_notes as Record<string, unknown> | undefined)?.[c];
                  const hint = typeof src === 'string' && src ? 'from ' + src
                    : typeof note === 'string' && note ? note : undefined;
                  return (
                    <td key={c} title={hint} style={{ ...S.sheetTd,
                          ...(i % 2 === 1 ? S.sheetTdAlt : null),
                          ...(SCORE_COLUMNS.has(c) ? S.sheetScore : null) }}>
                      {tone ? <span style={PILL[tone]}>{text}</span> : text}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* STATUS BAR. A `static` cell is a fact about the engine and is true with
          no backend. A `data` cell is a measurement or a live claim and reads '—'
          when absent — printing the design's "synced" here would be the surface
          asserting a sync that cannot exist yet. */}
      <div style={S.wbStatus}>
        {WORKBOOK_STATUS.map((cell, i) => {
          const raw = status[cell.key];
          const has = typeof raw === 'string' || typeof raw === 'number';
          const value = cell.kind === 'static' ? cell.value : (has ? String(raw) : '—');
          return (
            <span key={cell.key} style={i === 0 ? S.wbStatusFirst : undefined}>
              {cell.label ? `${cell.label}: ${value}` : value}
            </span>
          );
        })}
      </div>
    </div>
  );
}

// ── panels the design carries that are NOT yet transcribed ───────────────────
// NOT a placeholder pretending to be a page, and not a silent gap. It states the
// COUNT the design carries here, so a part-built sub-tab can never read as a
// finished one. A silent cap would let "covered everything" stand when it does
// not — the number is the honest report, and it goes to zero as they are built.
export function AwaitingPanels({ count, where, subTabs = 0 }: { count: number; where: string; subTabs?: number }) {
  if (!count) return null;
  return (
    <section style={S.awaiting}>
      <div style={S.awaitingHead}>
        <span style={{ ...S.dot, background: MC.amber }} />
        <span style={S.awaitingTitle}>{count} panel{count === 1 ? '' : 's'} not yet built</span>
      </div>
      <div style={S.awaitingBody}>
        The design carries {count} panel{count === 1 ? '' : 's'} for <strong>{where}</strong>
        {subTabs > 0 && <> across its {subTabs} sub-tabs — how they divide between
        those tabs is not known without the source, so the count is stated for the
        root rather than split by guess</>}. Their titles, columns and metric rows
        come from the design source, which is not on this machine — so they are
        counted here rather than invented. This line shrinks as they are built; it
        is a ledger, not a placeholder.
      </div>
    </section>
  );
}

// ── the live domain pulse ticker ─────────────────────────────────────────────
// Along the bottom, as pictured. It carries the SAME live source as the pulse
// panel and the same three voices: rows when there are rows, an explicit dormant
// line when the source answered zero, and the failure voice only on transport.
export function PulseTicker({ domain, index, err }: {
  domain: string; index: ActivityIndexResponse | null; err: string | null;
}) {
  const rows: ActivityRow[] = (index?.groups || [])
    .flatMap((g) => g.rows)
    .filter((r) => (r.domain || '').toUpperCase() === domain.toUpperCase());

  const tone = err ? MC.red : rows.length === 0 ? MC.amber : MC.green;
  const label = err ? 'PULSE UNAVAILABLE' : rows.length === 0 ? 'DOMAIN PULSE · DORMANT' : 'DOMAIN PULSE · LIVE';

  return (
    <footer style={S.ticker}>
      <span style={S.tickerHead}>
        <span style={{ ...S.dot, background: tone, boxShadow: `0 0 8px ${tone}` }} />
        <span style={{ ...S.tickerLabel, color: tone }}>{label}</span>
      </span>
      <span style={S.tickerBody}>
        {err ? (
          <span style={S.tickerDim}>{err}</span>
        ) : index === null ? (
          <span style={S.tickerDim}>reading…</span>
        ) : rows.length === 0 ? (
          <span style={S.tickerDim}>
            the source answered and reported no mutation in this window — rate 0 is the measurement
          </span>
        ) : (
          rows.slice(0, 6).map((r) => (
            <span key={r.journal_ref} style={S.tickerItem}>
              <span style={S.tickerWho}>{r.who}</span>
              <span style={S.tickerSubject}>{r.subject}</span>
              <span style={S.tickerSep}>·</span>
            </span>
          ))
        )}
      </span>
    </footer>
  );
}

const S: Record<string, React.CSSProperties> = {
  // ══ THE ROOT STATUS BANNER ════════════════════════════════════════════════
  // `rootNote` is the strip itself and its box is UNCHANGED from the one the
  // surface shipped — same font, same border, same padding, same radius. The
  // only addition is that it now lays its two slots out in a column.
  rootNote: {
    fontSize: 11, lineHeight: 1.55, color: MC.muted, borderRadius: 14,
    border: `1px solid ${MC.line2}`, background: 'rgba(255,255,255,.02)',
    padding: '12px 14px',
    display: 'flex', flexDirection: 'column', gap: 6,
  },
  // ⭐ FIXED HEIGHT, NOT min-height. This slot is present in all four states —
  // empty in the unread one — and it may not grow for a long reason: `nowrap` +
  // `hidden` mean a claim too long for the width is CLIPPED, never wrapped into
  // a second line that would push the page down. That is the property the
  // browser harness measures; jsdom has no layout and cannot see it.
  bannerClaim: {
    height: 18, lineHeight: '18px', display: 'flex', alignItems: 'center',
    gap: 7, overflow: 'hidden', flexShrink: 0,
  },
  bannerClaimText: {
    color: MC.text, fontSize: 11.5, fontWeight: 500,
    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0,
  },
  // The standing description. Present in every state, because what the root IS
  // stays true whether or not its agent is running.
  bannerBody: { color: MC.muted },
  // THE ROW ACTION'S CHROME ONLY. Its colour is whatever presentation.ts
  // already declares for that cell's value, spread over this — so the tone
  // vocabulary stays the single author of colour and the element is the only
  // thing that changed.
  rowAction: {
    fontFamily: MC_FONT, fontSize: 11, borderRadius: 9, padding: '5px 10px',
    borderWidth: 1, borderStyle: 'solid', borderColor: MC.line2,
    background: 'rgba(255,255,255,.04)', color: MC.muted,
  },
  panel: {
    border: `1px solid ${MC.panelEdge}`, background: MC.panel, borderRadius: MC.radius, padding: 18,
    boxShadow: `${MC.shadow}, inset 0 1px 0 ${MC.topHighlight}`,
    display: 'flex', flexDirection: 'column', gap: 12, minWidth: 0, fontFamily: MC_FONT,
  },
  panelHead: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 },
  panelTitleWrap: { display: 'flex', alignItems: 'center', gap: 9, minWidth: 0 },
  panelBar: { width: 3, height: 14, borderRadius: 2, flexShrink: 0 },
  panelTitle: { fontSize: 11.5, fontWeight: 700, letterSpacing: 1.1, color: MC.text, textTransform: 'uppercase' },
  chip: {
    display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 8.5, letterSpacing: 0.7,
    padding: '3px 9px', border: '1px solid', borderRadius: 999, whiteSpace: 'nowrap', fontWeight: 600,
  },
  chipDot: { width: 4, height: 4, borderRadius: 999 },
  // `boxSizing: border-box` IS LOAD-BEARING, not tidiness. The unread dot is
  // a 1px BORDER instead of a fill, and without this it measured 8x8 against
  // the filled dots' 6x6 — four rows of a roster sitting a pixel wider than
  // the rest. Caught by the geometry harness, which is the only place it
  // COULD be caught: jsdom has no layout.
  dot: { flexShrink: 0, width: 6, height: 6, borderRadius: 999, boxSizing: 'border-box' },
  // The title-bearing wrapper around the shared dot. It carries no size of its
  // own — the dot is the geometry, and a wrapper with padding here is how the
  // two surfaces would come to sit a pixel apart again.
  dotWrap: { display: 'inline-flex', flexShrink: 0, lineHeight: 0 },
  empty: { fontSize: 10.5, lineHeight: 1.5, color: MC.textDim, padding: '4px 0' },
  failed: { fontSize: 10.5, color: MC.red, padding: '4px 0' },
  dormant: { fontSize: 10.5, lineHeight: 1.55, color: MC.amber, padding: '4px 0' },

  // ── banner ──
  banner: {
    position: 'relative', overflow: 'hidden', display: 'flex', alignItems: 'center',
    justifyContent: 'space-between', gap: 16, borderRadius: MC.radius, padding: '18px 22px',
    background: MC.panel, border: `1px solid ${MC.panelEdge}`,
    boxShadow: `${MC.shadow}, inset 0 1px 0 ${MC.topHighlight}`, fontFamily: MC_FONT,
  },
  bannerGlow: {
    position: 'absolute', inset: 0, pointerEvents: 'none',
    background: `radial-gradient(560px 120px at 8% 0%, ${MC.tealSoft}, transparent 70%),
                 radial-gradient(420px 120px at 92% 100%, ${MC.violetSoft}, transparent 70%)`,
  },
  bannerText: { position: 'relative', display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 },
  bannerTitle: { fontSize: 16, fontWeight: 700, letterSpacing: -0.1, color: MC.text },
  bannerSub: { fontSize: 10.5, color: MC.textMuted, letterSpacing: 0.1, lineHeight: 1.45 },
  bannerRight: { position: 'relative', display: 'flex', alignItems: 'center', gap: 7, flexShrink: 0 },
  bannerState: { fontSize: 8.5, letterSpacing: 1.5, color: MC.textDim, fontWeight: 700 },

  // ── KPI ──
  kpiGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 10 },
  kpiTile: {
    border: `1px solid ${MC.panelEdgeSoft}`, borderRadius: 16, padding: '13px 15px',
    background: 'rgba(5,9,18,0.55)', boxShadow: `inset 0 1px 0 ${MC.topHighlight}`,
  },
  kpiLabel: { fontSize: 8, letterSpacing: 1.3, color: MC.textDim, fontWeight: 600 },
  kpiValue: { fontSize: 26, fontWeight: 700, marginTop: 6, fontVariantNumeric: 'tabular-nums', lineHeight: 1 },

  // ── chart ──
  chart: {
    display: 'flex', alignItems: 'flex-end', gap: 3, height: 64,
    padding: '0 2px', borderBottom: `1px solid ${MC.panelEdgeSoft}`,
  },
  barCol: { flex: 1, minWidth: 2, height: '100%', display: 'flex', alignItems: 'flex-end' },
  bar: { width: '100%', borderRadius: '3px 3px 0 0', transition: 'height 300ms' },

  // ── rows ──
  rows: { display: 'flex', flexDirection: 'column' },
  row: { display: 'flex', gap: 10, padding: '6px 0', borderTop: `1px solid ${MC.panelEdgeSoft}`, fontSize: 10.5 },
  rowWho: { color: MC.teal, minWidth: 78, fontWeight: 600 },
  rowSubject: { color: MC.textMuted, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  rowLocus: { color: MC.textDim, whiteSpace: 'nowrap' },

  // ── roster ──
  rosterGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(148px, 1fr))', gap: 10 },
  rosterCell: {
    border: `1px solid ${MC.panelEdgeSoft}`, borderRadius: 16, padding: '11px 13px',
    display: 'flex', alignItems: 'flex-start', gap: 8, background: 'rgba(5,9,18,0.55)',
    boxShadow: `inset 0 1px 0 ${MC.topHighlight}`,
  },
  rosterBody: { display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 },
  rosterName: { fontSize: 11, fontWeight: 700, letterSpacing: 1.1, color: MC.text },
  rosterState: { fontSize: 8.5, letterSpacing: 0.7, color: MC.textDim },

  code: { color: MC.teal },
  awaiting: {
    border: `1px dashed ${MC.amberSoft.replace('0.10', '0.35')}`, borderRadius: MC.radius,
    padding: 18, background: 'rgba(255,209,124,0.035)', display: 'flex',
    flexDirection: 'column', gap: 8, fontFamily: MC_FONT,
  },
  awaitingHead: { display: 'flex', alignItems: 'center', gap: 8 },
  awaitingTitle: { fontSize: 11.5, fontWeight: 700, letterSpacing: 0.8, color: MC.amber, textTransform: 'uppercase' },
  awaitingBody: { fontSize: 10.5, lineHeight: 1.6, color: MC.textMuted, maxWidth: 780 },
  panelTitleCol: { display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 },
  panelNote: { fontSize: 11, color: MC.muted, lineHeight: 1.45, fontWeight: 400, textTransform: 'none', letterSpacing: 0 },
  panelRight: { display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 },
  // `badgePlain` IS THE DEGRADE as well as a sampled visual — a badge the
  // vocabulary does not know renders exactly as the design's 18 untoned badges
  // do. The old `panelBadge` (hardcoded violet) is gone rather than kept as a
  // byte-identical twin: two names for one visual is the fault the slot ruling
  // exists to remove, one level down.
  badgePlain: {
    fontSize: 9, letterSpacing: 0.6, color: MC.textMuted, border: `1px solid ${MC.line2}`,
    background: 'rgba(255,255,255,.04)', borderRadius: 999, padding: '3px 9px',
    whiteSpace: 'nowrap', fontWeight: 600,
  },
  badgeAmber: {
    fontSize: 9, letterSpacing: 0.6, color: MC.amber, border: `1px solid ${MC.amber}`,
    background: MC.amberSoft, borderRadius: 999, padding: '3px 9px',
    whiteSpace: 'nowrap', fontWeight: 600,
  },
  badgeViolet: {
    fontSize: 9, letterSpacing: 0.6, color: MC.violet, border: `1px solid ${MC.violet}`,
    background: MC.violetSoft, borderRadius: 999, padding: '3px 9px',
    whiteSpace: 'nowrap', fontWeight: 600,
  },
  badgeGreen: {
    fontSize: 9, letterSpacing: 0.6, color: MC.green, border: `1px solid ${MC.green}`,
    background: MC.greenSoft, borderRadius: 999, padding: '3px 9px',
    whiteSpace: 'nowrap', fontWeight: 600,
  },
  badgeRed: {
    fontSize: 9, letterSpacing: 0.6, color: MC.red, border: `1px solid ${MC.red}`,
    background: MC.redSoft, borderRadius: 999, padding: '3px 9px',
    whiteSpace: 'nowrap', fontWeight: 600,
  },
  tableWrap: { overflowX: 'auto', border: `1px solid ${MC.line2}`, borderRadius: 14 },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 11 },
  th: {
    textAlign: 'left', padding: '9px 12px', color: MC.muted, fontWeight: 700,
    fontSize: 9.5, letterSpacing: 0.9, textTransform: 'uppercase',
    borderBottom: `1px solid ${MC.line2}`, whiteSpace: 'nowrap', background: 'rgba(255,255,255,.02)',
  },
  td: { padding: '9px 12px', color: MC.text, borderBottom: `1px solid ${MC.line2}` },
  tdEmpty: { padding: '14px 12px', color: MC.dim, fontSize: 10.5, lineHeight: 1.5 },
  kpiSub: { fontSize: 9.5, color: MC.dim, marginTop: 3, lineHeight: 1.35 },
  kpiSince: { fontSize: 9, color: MC.dim, marginTop: 2, lineHeight: 1.3, opacity: 0.8 },
  metrics: { display: 'flex', flexDirection: 'column' },
  metricRow: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
    padding: '9px 2px', borderBottom: `1px solid ${MC.line2}`, fontSize: 11,
  },
  metricName: { color: MC.muted },
  metricValue: { fontSize: 12, fontWeight: 700, fontVariantNumeric: 'tabular-nums' },
  checklist: { listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 7 },
  checkItem: { display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 11, color: MC.text, lineHeight: 1.45 },
  checkTick: { color: MC.green, flexShrink: 0, fontSize: 10 },
  pillStack: { display: 'flex', flexWrap: 'wrap', gap: 6 },
  pill: {
    fontSize: 9.5, letterSpacing: 0.5, color: MC.teal, border: `1px solid ${MC.tealEdge}`,
    background: MC.tealSoft, borderRadius: 999, padding: '4px 10px', fontWeight: 600,
  },
  noteBox: {
    fontSize: 10.5, lineHeight: 1.55, color: MC.muted, borderRadius: 14,
    border: `1px solid ${MC.line2}`, background: 'rgba(255,255,255,.02)', padding: '11px 13px',
  },
  hourBoard: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(132px, 1fr))', gap: 8 },
  hourNode: {
    border: '1px solid', borderRadius: 12, padding: '9px 10px',
    display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0,
  },
  hourTime: { fontSize: 10, fontWeight: 700, letterSpacing: 0.6, fontVariantNumeric: 'tabular-nums' },
  hourTitle: { fontSize: 11, color: MC.text, fontWeight: 600, lineHeight: 1.3 },
  hourNote: { fontSize: 9.5, color: MC.dim, lineHeight: 1.35 },
  // The daily CEO report preview.
  report: { display: 'flex', flexDirection: 'column', gap: 6 },
  reportHead: { display: 'flex', alignItems: 'baseline', gap: 8 },
  reportDay: { fontSize: 12, color: MC.blue, fontWeight: 700 },
  reportAt: { fontSize: 9.5, color: MC.dim, letterSpacing: 0.3 },
  // AN EMPTY WINDOW IS THE REPORT, not a failure — so it is toned down rather
  // than dressed up as an error, and never rendered as a blank body.
  reportEmptyBody: { fontSize: 10.5, color: MC.dim, lineHeight: 1.5, margin: 0, fontStyle: 'italic' },
  reportMore: { fontSize: 9, color: MC.dim, letterSpacing: 0.3 },
  timeline: { display: 'flex', flexDirection: 'column', gap: 10 },
  event: { display: 'flex', flexDirection: 'column', gap: 3, borderLeft: `2px solid ${MC.tealEdge}`, paddingLeft: 11 },
  eventWhen: { fontSize: 11, color: MC.teal, fontWeight: 700 },
  // THE SOURCE LINE sits between the time and the note, as it does on hers:
  // dimmer than the note it qualifies, so the note stays the focal element.
  eventMeta: { fontSize: 9.5, color: MC.dim, letterSpacing: 0.3 },
  eventWho: { fontWeight: 700 },
  eventText: { fontSize: 10.5, color: MC.muted, lineHeight: 1.5 },
  reportGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(210px, 1fr))', gap: 10 },
  reportCard: {
    border: `1px solid ${MC.line2}`, borderRadius: 16, padding: '12px 14px',
    background: 'rgba(255,255,255,.02)', display: 'flex', flexDirection: 'column', gap: 6,
  },
  reportTitle: { fontSize: 12, margin: 0, color: MC.text, fontWeight: 700 },
  reportBody: { fontSize: 10.5, margin: 0, color: MC.muted, lineHeight: 1.5 },
  reportMeta: { display: 'flex', justifyContent: 'space-between', fontSize: 9.5, color: MC.dim },
  ledger: { display: 'flex', flexDirection: 'column' },
  ledgerRow: {
    display: 'flex', alignItems: 'flex-start', gap: 11, padding: '9px 0',
    borderBottom: `1px solid ${MC.line2}`,
  },
  ledgerTime: { fontSize: 10, color: MC.dim, fontVariantNumeric: 'tabular-nums', flexShrink: 0, marginTop: 1 },
  ledgerBody: { display: 'flex', flexDirection: 'column', gap: 2, flex: 1, minWidth: 0 },
  ledgerName: { fontSize: 11, color: MC.text, fontWeight: 700, letterSpacing: 0.3 },
  ledgerDetail: { fontSize: 10, color: MC.muted },
  ledgerState: { fontSize: 9.5, color: MC.dim, flexShrink: 0 },
  statShell: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(88px, 1fr))', gap: 8 },
  statBlock: {
    border: `1px solid ${MC.line2}`, borderRadius: 12, padding: '10px 11px',
    background: 'rgba(255,255,255,.02)', display: 'flex', flexDirection: 'column', gap: 3,
  },
  statLabel: { fontSize: 9, color: MC.dim, letterSpacing: 0.5 },
  statValue: { fontSize: 15, fontWeight: 700, fontVariantNumeric: 'tabular-nums' },
  chipRibbon: { display: 'flex', flexWrap: 'wrap', gap: 7 },
  serviceChip: {
    display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 10, color: MC.text,
    border: '1px solid', borderRadius: 999, padding: '5px 11px', background: 'rgba(255,255,255,.02)',
  },
  chipLed: { width: 6, height: 6, borderRadius: 999, flexShrink: 0 },
  flow: { display: 'flex', alignItems: 'stretch', gap: 6, flexWrap: 'wrap' },
  flowStep: {
    flex: '1 1 130px', minWidth: 118, border: '1px solid', borderRadius: 13, padding: '10px 11px',
    display: 'flex', flexDirection: 'column', gap: 3,
  },
  flowLabel: { fontSize: 11, color: MC.text, fontWeight: 700, lineHeight: 1.3 },
  flowSub: { fontSize: 9.5, color: MC.dim, lineHeight: 1.35 },
  flowArrow: { alignSelf: 'center', color: MC.dim, fontSize: 15, flexShrink: 0 },
  funnel: { display: 'flex', flexDirection: 'column', gap: 8 },
  funnelRow: { display: 'flex', alignItems: 'center', gap: 10, fontSize: 11 },
  funnelName: { color: MC.muted, width: 156, flexShrink: 0 },
  // .v10-funnel-row>div — height:12px, rgba(255,255,255,.06). The bar's
  // teal->blue gradient below was already the design's; only the track drifted.
  funnelTrack: { flex: 1, height: 12, borderRadius: 99, background: 'rgba(255,255,255,.06)', overflow: 'hidden' },
  funnelBar: { height: '100%', borderRadius: 999, background: 'linear-gradient(90deg,#36e6d4,#41a7ff)' },
  funnelValue: { width: 46, textAlign: 'right', fontWeight: 700, fontVariantNumeric: 'tabular-nums' },
  taskList: { display: 'flex', flexDirection: 'column', gap: 6 },
  task: {
    display: 'flex', alignItems: 'flex-start', gap: 9, padding: '9px 10px',
    border: `1px solid ${MC.line2}`, borderRadius: 12, background: 'rgba(255,255,255,.02)',
  },
  taskBox: {
    width: 15, height: 15, borderRadius: 5, border: '1px solid', flexShrink: 0,
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 9,
  },
  taskBody: { display: 'flex', flexDirection: 'column', gap: 2, flex: 1, minWidth: 0 },
  taskWhen: { fontSize: 11, color: MC.text, fontWeight: 700 },
  taskNote: { fontSize: 9.5, color: MC.dim },
  taskFit: { fontSize: 10, color: MC.muted, fontWeight: 600 },
  taskState: { fontSize: 9.5, color: MC.muted, fontStyle: 'normal', flexShrink: 0 },
  // A pressable row keeps the row's own look; only a button's defaults are reset.
  taskPress: { width: '100%', textAlign: 'left', font: 'inherit', color: 'inherit', cursor: 'pointer' },
  scriptSheet: {
    background: '#f7f9fb', color: '#13293f', borderRadius: 18, padding: 28,
    boxShadow: '0 18px 60px rgba(0,0,0,.26)', display: 'flex', flexDirection: 'column', gap: 16,
  },
  scriptEmpty: { fontSize: 12, color: '#5a728a', lineHeight: 1.6 },
  scriptFit: { fontSize: 13, color: '#13293f', fontWeight: 700, letterSpacing: '.02em' },
  scriptBlock: { display: 'flex', flexDirection: 'column', gap: 6 },
  scriptHeading: { fontSize: 13, color: '#13293f', fontWeight: 700 },
  scriptSay: { margin: 0, fontSize: 13, lineHeight: 1.7, color: '#20364c' },
  scriptListen: { margin: 0, fontSize: 12, lineHeight: 1.6, color: '#4a6076' },
  scriptDirection: { margin: 0, fontSize: 12, lineHeight: 1.6, color: '#4a6076', fontStyle: 'italic' },
  scriptBranchDirection: { fontSize: 11.5, lineHeight: 1.55, color: '#4a6076', fontStyle: 'italic', marginBottom: 8 },
  scriptBody: { display: 'flex', gap: 22, alignItems: 'flex-start' },
  scriptLines: { flex: '1 1 auto', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 16 },
  scriptChips: { display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 2 },
  scriptChip: {
    background: 'transparent', color: '#4a6076', border: '1px solid #ccd8e2',
    borderRadius: 999, padding: '2px 10px', fontSize: 11, cursor: 'pointer', font: 'inherit',
  },
  scriptChipOpen: {
    background: '#13293f', color: '#f7f9fb', border: '1px solid #13293f',
    borderRadius: 999, padding: '2px 10px', fontSize: 11, cursor: 'pointer', font: 'inherit',
  },
  scriptBranch: {
    marginTop: 8, padding: '10px 12px', background: '#eef3f8',
    border: '1px solid #dce5ed', borderRadius: 12,
  },
  scriptTrigger: { fontSize: 11, color: '#5a728a', marginBottom: 8 },
  scriptMove: { display: 'flex', gap: 10, marginBottom: 8 },
  scriptMoveName: {
    fontSize: 10, color: '#13293f', minWidth: 74, paddingTop: 3,
    letterSpacing: '.06em', textTransform: 'uppercase', fontWeight: 700,
  },
  scriptMoveSay: { fontSize: 12.5, lineHeight: 1.6, color: '#20364c' },
  scriptReturn: { fontSize: 11, color: '#5a728a' },
  scriptAside: {
    flex: '0 0 280px', background: '#eef3f8', border: '1px solid #dce5ed',
    borderRadius: 14, padding: 14,
  },
  scriptAsideTitle: { fontSize: 10, letterSpacing: '.08em', color: '#5a728a', marginBottom: 8 },
  scriptAsideItem: { fontSize: 12, lineHeight: 1.55, color: '#20364c', marginBottom: 8 },
  // ── THE RECORD CARD. Every value sampled from the design's own stylesheet:
  //    .record-card{padding:10px;border:1px solid var(--line2);
  //                 background:rgba(255,255,255,.028);border-radius:12px;margin-top:8px}
  //    .record-card b{font-size:11px}
  //    .record-card small{display:block;color:var(--muted);font-size:9px;margin-top:3px}
  // ── THE BACKEND TABLE. Two rules, and they are the whole difference:
  //    .backend-table td:first-child{font-weight:700}
  //    .backend-table .sub{display:block;color:var(--muted);font-size:10px;margin-top:3px}
  tdFirst: { fontWeight: 700 },
  cellSub: { display: 'block', color: MC.textMuted, fontSize: 10, marginTop: 3 },
  // ── THE LIVE CALL CONSOLE, every value from the design's own sheet.
  //    .live-call-shell{display:grid;grid-template-columns:1fr 1fr;gap:10px}
  //    .call-tile{padding:11px;border:1px solid var(--line2);border-radius:12px;
  //               background:rgba(65,167,255,.045)}
  //    .message-console{display:grid;gap:7px}
  //    .message-row{display:grid;grid-template-columns:70px 1fr auto;gap:9px;
  //                 align-items:center;padding:9px 10px;border-radius:11px}
  callShell: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 },
  callTile: {
    padding: 11, border: `1px solid ${MC.line2}`, borderRadius: 12,
    background: 'rgba(65,167,255,.045)',
  },
  callTileLabel: { fontSize: 9.5, color: MC.textMuted, letterSpacing: 0.3 },
  callTileValue: { display: 'block', fontSize: 19, fontWeight: 800, margin: '4px 0 2px' },
  callTileSub: { display: 'block', fontSize: 9, color: MC.dim },
  messageConsole: { display: 'grid', gap: 7, marginTop: 10 },
  messageRow: {
    display: 'grid', gridTemplateColumns: '70px 1fr auto', gap: 9, alignItems: 'center',
    padding: '9px 10px', borderRadius: 11, border: `1px solid ${MC.line2}`,
  },
  messageTime: { fontSize: 9.5, color: MC.dim },
  messageBody: { display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 },
  messageTitle: { fontSize: 11, color: MC.text },
  messageDetail: { fontSize: 9.5, color: MC.textMuted },
  recordCard: {
    padding: 10, border: `1px solid ${MC.line2}`, background: 'rgba(255,255,255,.028)',
    borderRadius: 12, marginTop: 8,
  },
  recordName: { fontSize: 11, color: MC.text },
  recordDetail: { display: 'block', color: MC.textMuted, fontSize: 9, marginTop: 3 },
  questionList: { display: 'flex', flexDirection: 'column', gap: 8 },
  // THE DESIGN'S OWN GRID — `34px 1fr 150px`, the number column ahead of the
  // body and a fixed column for the tag, so the tags line up down the panel
  // instead of ragging with the question length.
  question: {
    display: 'grid', gridTemplateColumns: '34px 1fr 150px', alignItems: 'start', gap: 10,
    border: `1px solid ${MC.line2}`, borderRadius: 13, padding: '11px 12px',
    background: 'rgba(255,255,255,.02)',
  },
  // the design's counter circle
  questionNo: {
    height: 26, width: 26, borderRadius: '50%', display: 'grid', placeItems: 'center',
    background: 'rgba(22,139,133,.22)', color: '#8ef2e9', fontWeight: 800, fontSize: 11,
  },
  questionBody: { display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 },
  questionText: { fontSize: 11.5, color: MC.text, fontWeight: 600, lineHeight: 1.45 },
  questionTests: { fontSize: 10, color: MC.dim },
  // ── THE WORKBOOK. Every value sampled from the design's stylesheet via
  //    workbook.ts. The sheet is LIGHT deliberately — it is a spreadsheet, not
  //    page chrome. See the note at the top of workbook.ts before darkening it.
  workbookShell: {
    border: `1px solid ${WB.shellBorder}`, background: WB.shellBg, borderRadius: 18,
    overflow: 'hidden', boxShadow: MC.cardShadow,
  },
  wbToolbar: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10,
    flexWrap: 'wrap', padding: '12px 14px', background: WB.toolbarBg,
    borderBottom: `1px solid ${WB.toolbarLine}`,
  },
  wbFile: { display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 },
  wbIcon: {
    width: 34, height: 34, borderRadius: 8, display: 'grid', placeItems: 'center',
    background: WB.iconBg, color: '#ffffff', fontWeight: 900, fontSize: 15, flexShrink: 0,
  },
  wbFileName: { fontSize: 12, color: MC.text },
  wbFilePath: { display: 'block', color: WB.fileSub, marginTop: 2, fontSize: 10 },
  wbActions: { display: 'flex', gap: 7, flexWrap: 'wrap' },
  wbAction: {
    fontFamily: MC_FONT, border: `1px solid ${WB.actionBorder}`, background: WB.actionBg,
    color: WB.actionText, borderRadius: 9, padding: '8px 10px', fontSize: 11,
    // inert by construction: no handler, and it says so on hover
    cursor: 'not-allowed', opacity: 0.55,
  },
  wbFormulaRow: {
    display: 'grid', gridTemplateColumns: '65px 40px 1fr', alignItems: 'center',
    background: WB.formulaBg, borderBottom: `1px solid ${WB.divider}`,
  },
  wbNameBox: {
    textAlign: 'center', color: WB.nameBox, padding: 9, fontSize: 11,
    borderRight: `1px solid ${WB.divider}`,
  },
  wbFx: {
    textAlign: 'center', fontStyle: 'italic', color: WB.fxText, padding: 9, fontSize: 11,
    borderRight: `1px solid ${WB.divider}`,
  },
  wbFormula: {
    fontFamily: 'ui-monospace, SFMono-Regular, Consolas, monospace', fontSize: 11,
    color: WB.formulaText, whiteSpace: 'nowrap', overflow: 'hidden',
    textOverflow: 'ellipsis', padding: 9,
  },
  sheetTabs: {
    display: 'flex', gap: 2, padding: '8px 10px 0', background: WB.tabsBg,
    borderBottom: `1px solid ${WB.divider}`, overflowX: 'auto',
  },
  sheetTab: {
    fontFamily: MC_FONT, fontSize: 11, padding: '8px 12px', border: 'none',
    borderRadius: '8px 8px 0 0', cursor: 'pointer', whiteSpace: 'nowrap', fontWeight: 600,
  },
  sheetBody: { background: WB.sheetBg, overflow: 'auto', maxHeight: 510 },
  sheetTable: {
    minWidth: 1200, borderCollapse: 'separate', borderSpacing: 0,
    fontSize: 11, color: WB.sheetText,
  },
  sheetTh: {
    position: 'sticky', top: 0, zIndex: 2, textAlign: 'left', padding: '8px 11px',
    fontSize: 11, fontWeight: 700, color: WB.thText, background: WB.thBg,
    borderRight: `1px solid ${WB.thRight}`, borderBottom: `1px solid ${WB.thBottom}`,
    whiteSpace: 'nowrap', textTransform: 'none', letterSpacing: 0,
  },
  sheetTd: {
    padding: '9px 10px', color: WB.tdText, background: WB.tdBg,
    borderRight: `1px solid ${WB.tdLine}`, borderBottom: `1px solid ${WB.tdLine}`,
  },
  sheetTdAlt: { background: WB.tdAlt },
  // THE ROW-NUMBER GUTTER — sticky on the left, above the cells, as Excel has it.
  rowHead: {
    position: 'sticky', left: 0, zIndex: 3, width: 42, textAlign: 'center',
    background: WB.rowheadBg, color: WB.rowheadText, fontWeight: 700,
  },
  rowHeadTh: { zIndex: 4, width: 42, background: WB.rowheadBg },
  sheetScore: { background: WB.scoreBg, fontWeight: 900, color: WB.scoreText },
  sheetEmpty: {
    padding: '16px 12px', fontSize: 11, color: '#5c7368', background: WB.tdBg,
    borderBottom: `1px solid ${WB.tdLine}`,
  },
  wbStatus: {
    display: 'flex', gap: 18, padding: '8px 12px', background: WB.statusBg,
    color: WB.statusText, fontSize: 10, overflowX: 'auto', whiteSpace: 'nowrap',
  },
  wbStatusFirst: { color: '#ffffff', fontWeight: 800 },
  // ── THE ROLE BANNER ──────────────────────────────────────────────────────
  roleBanner: {
    padding: '14px 16px', border: '1px solid rgba(54,230,212,.24)',
    background: 'linear-gradient(90deg,rgba(54,230,212,.08),rgba(65,167,255,.05))',
    borderRadius: 18, display: 'flex', alignItems: 'center',
    justifyContent: 'space-between', gap: 12, flexWrap: 'wrap',
  },
  roleBannerTitle: { fontSize: 14, color: MC.text },
  roleBannerText: { display: 'block', color: MC.muted, marginTop: 3, fontSize: 10.5, lineHeight: 1.55 },
  roleBannerRight: { display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' },
  pillPlain: {
    display: 'inline-block', padding: '5px 8px', borderRadius: 999, fontSize: 11,
    background: 'rgba(255,255,255,.07)', border: `1px solid ${MC.line2}`, color: '#e9f5ff',
  },
  pillGreen: {
    display: 'inline-block', padding: '5px 8px', borderRadius: 999, fontSize: 11,
    background: 'rgba(140,255,183,.1)', border: '1px solid rgba(140,255,183,.25)', color: '#d3ffe0',
  },
  pillAmber: {
    display: 'inline-block', padding: '5px 8px', borderRadius: 999, fontSize: 11,
    background: 'rgba(255,209,124,.1)', border: '1px solid rgba(255,209,124,.25)', color: '#ffe3ad',
  },
  pillRed: {
    display: 'inline-block', padding: '5px 8px', borderRadius: 999, fontSize: 11,
    background: 'rgba(255,100,100,.1)', border: '1px solid rgba(255,100,100,.25)', color: '#ffd2d2',
  },
  pillTeal: {
    display: 'inline-block', padding: '5px 8px', borderRadius: 999, fontSize: 11,
    background: 'rgba(54,230,212,.1)', border: '1px solid rgba(54,230,212,.25)', color: '#c9fff8',
  },
  slideGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 10 },
  slideThumb: {
    aspectRatio: '16 / 9', border: `1px solid ${MC.line}`, borderRadius: 13, padding: 14,
    background: 'linear-gradient(145deg,#f7fafc,#e7f1f4)', color: '#0b2942',
    boxShadow: '0 12px 34px rgba(0,0,0,.18)', position: 'relative',
    display: 'flex', flexDirection: 'column', gap: 4, overflow: 'hidden',
  },
  slideNo: { position: 'absolute', top: 8, right: 11, fontSize: 9, color: '#5b7d95', fontWeight: 700 },
  slideTitle: { fontSize: 11.5, fontWeight: 700, lineHeight: 1.35 },
  slideNote: { fontSize: 9.5, color: '#456079', lineHeight: 1.4 },
  suggestions: { display: 'flex', flexDirection: 'column', gap: 9 },
  suggestion: {
    border: `1px solid ${MC.line2}`, borderRadius: 13, padding: '11px 12px',
    background: 'rgba(255,255,255,.02)', display: 'flex', flexDirection: 'column', gap: 4,
  },
  suggestionTitle: { fontSize: 11, color: MC.teal, fontWeight: 700 },
  suggestionBody: { margin: 0, fontSize: 10.5, color: MC.muted, lineHeight: 1.5 },
  inboxShell: {
    border: `1px solid ${MC.line2}`, borderRadius: 16, overflow: 'hidden',
    background: 'rgba(255,255,255,.02)', display: 'flex', flexDirection: 'column',
  },
  mailRow: { display: 'flex', gap: 12, padding: '10px 13px', borderBottom: `1px solid ${MC.line2}`, fontSize: 11 },
  mailFrom: { color: MC.text, width: 150, flexShrink: 0, fontWeight: 700 },
  mailSubject: { color: MC.muted, flex: 1, minWidth: 0 },
  mailState: { color: MC.dim, flexShrink: 0 },
  calendarShell: {
    border: `1px solid ${MC.line2}`, borderRadius: 16, background: 'rgba(255,255,255,.02)',
    display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 1, overflow: 'hidden',
  },
  calDay: { padding: '11px 12px', display: 'flex', flexDirection: 'column', gap: 5, minHeight: 92 },
  calDayName: { fontSize: 10, color: MC.teal, fontWeight: 700, letterSpacing: 0.6 },
  calEntries: { fontSize: 10, color: MC.muted, lineHeight: 1.5 },
  volume: { display: 'flex', alignItems: 'flex-end', gap: 5, height: 132 },
  volCol: { flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5, height: '100%' },
  volTrack: { flex: 1, width: '100%', display: 'flex', alignItems: 'flex-end' },
  volBar: { width: '100%', borderRadius: '4px 4px 0 0' },
  volLabel: { fontSize: 9, color: MC.dim },
  transcripts: { display: 'flex', flexDirection: 'column', gap: 8 },
  transcript: { border: `1px solid ${MC.line2}`, borderRadius: 13, padding: '10px 12px', background: 'rgba(255,255,255,.02)' },
  transcriptHead: { display: 'flex', gap: 10, alignItems: 'center', cursor: 'pointer', fontSize: 11, color: MC.text },
  transcriptSub: { color: MC.muted, flex: 1, minWidth: 0 },
  transcriptState: { color: MC.dim, fontSize: 9.5 },
  transcriptBody: { whiteSpace: 'pre-wrap', fontSize: 10.5, color: MC.muted, lineHeight: 1.6, marginTop: 9 },
  pluginGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))', gap: 10 },
  pluginCard: {
    border: `1px solid ${MC.line2}`, borderRadius: 14, padding: '11px 12px',
    background: 'rgba(255,255,255,.02)', display: 'flex', flexDirection: 'column', gap: 5,
  },
  pluginHead: { display: 'flex', alignItems: 'center', gap: 8 },
  pluginName: { fontSize: 11, color: MC.text, fontWeight: 700 },
  pluginMeta: { fontSize: 9.5, color: MC.dim, lineHeight: 1.4 },
  pre: {
    fontSize: 9.5, color: MC.textMuted, margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
    maxHeight: 220, overflow: 'auto', fontFamily: MC_FONT,
  },

  // ── ticker ──
  ticker: {
    flexShrink: 0, display: 'flex', alignItems: 'center', gap: 14, overflow: 'hidden',
    background: MC.panel, border: `1px solid ${MC.panelEdge}`, borderRadius: 999,
    padding: '9px 16px', fontFamily: MC_FONT, boxShadow: `inset 0 1px 0 ${MC.topHighlight}`,
  },
  tickerHead: { display: 'flex', alignItems: 'center', gap: 7, flexShrink: 0 },
  tickerLabel: { fontSize: 8.5, letterSpacing: 1.5, fontWeight: 700 },
  tickerBody: { display: 'flex', alignItems: 'center', gap: 8, minWidth: 0, overflow: 'hidden', whiteSpace: 'nowrap' },
  tickerDim: { fontSize: 10, color: MC.textDim },
  tickerItem: { display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 10 },
  tickerWho: { color: MC.teal, fontWeight: 600 },
  tickerSubject: { color: MC.textMuted },
  tickerSep: { color: MC.textFaint },
};

// The design's five pill tones. `plain` is the UNTONED pill — a real third
// visual level between a coloured pill and bare text, not an absence.
const PILL: Record<Visual, React.CSSProperties> = {
  green: S.pillGreen, amber: S.pillAmber, red: S.pillRed,
  teal: S.pillTeal, plain: S.pillPlain,
  // THE BADGE ELEMENT — the design's smaller, letter-spaced chip. Five tones
  // where there used to be one hardcoded violet, and `badgePlain` doubles as the
  // degrade so an unknown badge looks like the design's own untoned default.
  badgePlain: S.badgePlain, badgeAmber: S.badgeAmber, badgeViolet: S.badgeViolet,
  badgeGreen: S.badgeGreen, badgeRed: S.badgeRed,
};
